import { withoutUnavailableChoices } from "../../utils/modifierAvailability";
import { cs } from "../../utils/currency";
import { readableTextColor } from "../../utils/readableTextColor";
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import api, { ApiResponse, getCodes } from "../../services/api";
import vegIcon from "../../assests/Veg_symbol.png";
import nonvegIcon from "../../assests/Non_veg_symbol.png";
import { getCategories } from "../../services/api";
import LoyaltyBar from "../../components/Loyaltybar";
import VideoLoader from "../../components/VideoLoader";
// import "../../styles/pos.css";
import { useOrder, OrderItem } from "./OrderContext";
import { BadgePercent, Pencil, ShoppingCart } from "lucide-react";
import LeftArrow from "../../assests/left-arrow.svg";
import { categoryCodes, TaxesProps, useAuth } from "../../context/AuthContext";
import { useKitchenAuth } from "../../context/KitchenAuthContext";
import LocationBadge from "../../components/LocationBadge";
import DealsModal from "./DealsModal";
import ConfirmModal from "./ConfirmModal";
import { getPlatformLogo } from "../../services/api";
interface CustomizeChoice {
  label: string;
  price: number;
  image?: string;
  default?: boolean;
}

interface PortionOption {
  key: string;
  label: string;
  icon?: string;
}

interface CustomizeGroup {
  name: string;
  required: boolean;
  multiple?: boolean;
  multipleAddons?: boolean;
  choices: CustomizeChoice[];
  noOfFreeToppings?: number;
  maxToppingsAllowed?: number;
  minAddons?: number;
  displayType?: "checkbox" | "quantity" | "portion" | "radio";
  quantityOptions?: number[];
  portionOptions?: PortionOption[];
  showPortionSelector?: boolean;
}
interface AppliedDeal {
  dealId: string;
  originalDealId?: string;
  name: string;
  discountAmount: number;
  dealType: DealType;
  byoBasePrice?: number;
  byoIncludedToppings?: number;
  bundlePrice?: number;
  bundleFixedItemsTotal?: number;
}

type DealType = "percentage_discount" | "fixed_price" | "buy_x_get_y" | "build_your_own" | "bundle";

interface MenuItem {
  _id: string;
  name: string;
  kitchenName?: string;
  description: string;
  price: number;
  categoryId: string;
  category: string;
  additionalCategoryIds?: string[];
  categoryCode?: string;
  imageUrl?: string;
  customize: CustomizeGroup[];
  tags?: string[];
  foodType: "veg" | "non-veg" | "both";
}
interface CartItem {
  item: MenuItem;
  quantity: number;
}
interface Category {
  name: string;
  imageUrl: string;
  _id: string;
  categoryCode: string;
}
interface LoyaltyCustomer {
  _id: string;
  customerId: string;
  phoneNumber: number;
  points: number;
}
interface CustomSelectionsWithSections {
  [key: string]: number | string;
}

// Pizza Icons component matching the screenshot design
const PizzaIcons = {
  left: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className="w-4 h-4"
    >
      <circle
        cx="8"
        cy="8"
        r="6.5"
        stroke="currentColor"
        strokeWidth="1"
        fill="white"
      />
      <path d="M8 1.5 L8 14.5" stroke="currentColor" strokeWidth="0.8" />
      <path
        d="M1.5 8 A6.5 6.5 0 0 1 8 1.5 L8 14.5 A6.5 6.5 0 0 1 1.5 8"
        fill="currentColor"
      />
    </svg>
  ),
  right: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className="w-4 h-4"
    >
      <circle
        cx="8"
        cy="8"
        r="6.5"
        stroke="currentColor"
        strokeWidth="1"
        fill="white"
      />
      <path d="M8 1.5 L8 14.5" stroke="currentColor" strokeWidth="0.8" />
      <path
        d="M14.5 8 A6.5 6.5 0 0 1 8 14.5 L8 1.5 A6.5 6.5 0 0 1 14.5 8"
        fill="currentColor"
      />
    </svg>
  ),
  whole: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className="w-4 h-4"
    >
      <circle
        cx="8"
        cy="8"
        r="6.5"
        stroke="currentColor"
        strokeWidth="1"
        fill="currentColor"
      />
    </svg>
  ),
};
// Returns total price discount for free toppings (per-group noOfFreeToppings)
const computeFreeToppingDiscount = (
  item: MenuItem,
  selections: Record<string, number | string>,
): number => {
  let total = 0;
  for (const group of item.customize) {
    if (group.required || group.multiple) continue;
    const freeToppings = group.noOfFreeToppings ?? 0;
    if (!freeToppings) continue;
    let freeRemaining = freeToppings;
    for (const choice of group.choices) {
      if (freeRemaining <= 0) break;
      if (choice.default) continue;
      const key = `${group.name}|||${choice.label}`;
      const qty = Number(selections[key] || 0);
      if (qty > 0) {
        const freeQty = Math.min(qty, freeRemaining);
        total += (choice.price || 0) * freeQty;
        freeRemaining -= freeQty;
      }
    }
  }
  return total;
};

// Per-unit tax of a cart item. When the rate is split into GST/PST, `tax`
// already equals gst + pst, so it must not be added on top of them.
const unitTax = (i: { tax?: number; gst?: number; pst?: number }) =>
  (i.gst || 0) + (i.pst || 0) > 0 ? (i.gst || 0) + (i.pst || 0) : i.tax || 0;



// Veg / non-veg marks on menu cards — hidden for now; set true to show again
const SHOW_FOOD_TYPE_ICON = false;
/** One pick in a deal: the customer chooses one of `options` */
type DealSlot = {
  name?: string;
  options: string[];
  upcharges?: { itemId: string; amount: number }[];
  preselections: { groupName: string; choiceLabel: string }[];
};

const POS: React.FC = () => {
  const [selectedCustomizeItem, setSelectedCustomizeItem] =
    useState<MenuItem | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appliedDeals, setAppliedDeals] = useState<AppliedDeal[]>([]);
  const [phoneNumber, setPhoneNumber] = useState("");
  const { setCategoryCodes, setTaxes, categoryCodes, taxes, currencySymbol } = useAuth();
  const { user: kitchenUser } = useKitchenAuth();
  const restaurantId = kitchenUser?.restaurantId || "";
  const [tableToken, setTableToken] = useState<number | "">("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [points, setPoints] = useState<number>(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    const fromSession = window.sessionStorage.getItem("pos_logo_url") || "";
    if (fromSession) return fromSession;
    return window.localStorage.getItem("pos_logo_url") || "";
  });
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [isLogoLoaded, setIsLogoLoaded] = useState(false);

  const [showcustModal, setShowcustModal] = useState(false);
  const [customizingItem, setCustomizingItem] = useState<OrderItem | null>(
    null,
  );
  const [editingDealMeta, setEditingDealMeta] = useState<{
    dealType: DealType;
    byoBasePrice?: number;
    byoIncludedToppings?: number;
    bundlePrice?: number;
    bundleFixedItemsTotal?: number;
  } | null>(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Build Your Own deal context
  const [activeBYODeal, setActiveBYODeal] = useState<{
    claimId: string;   // unique per claim — used to tag cart items & appliedDeals entry
    dealId: string;    // actual MongoDB deal _id
    dealName: string;
    basePrice: number;
    includedToppings: number;
    minPizzas: number;
    pizzasDone: number;
    itemIds: string[];
  } | null>(null);
  // Pizza picker for BYO deals with multiple eligible items
  const [byoPicker, setByoPicker] = useState<{ pizzaIndex: number } | null>(null);

  // Bundle deal context
  const [activeBundleDeal, setActiveBundleDeal] = useState<{
    claimId: string;          // unique per claim
    dealId: string;           // actual MongoDB deal _id
    dealName: string;
    bundlePrice: number;
    fixedItemsTotal: number;
    slots: DealSlot[];
    currentSlotIndex: number; // which slot we're on (0-based)
    pizzaBasePricesSum: number; // accumulated base prices of slots already added
    pricingMode: "fixed" | "cheapest_free" | "percent_off";
    discountPercent: number;
    basePrices: number[]; // base price of each picked item (for "cheapest free")
    upchargeTotal: number; // extra charges for particular picked items
  } | null>(null);

  // Deals shown in the menu (usable now at this location)
  const [deals, setDeals] = useState<any[]>([]);
  useEffect(() => {
    if (!restaurantId) return;
    api
      .get("/admin/getDeals", { params: { restaurantId, orderType: "kiosk" } })
      .then((res) => setDeals(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setDeals([]));
  }, [restaurantId]);

  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isDrawer, setIsDrawer] = useState(false);
  const navigate = useNavigate();
  const {
    orderData,
    setOrderData,
    discount,
    setDiscount,
    logoUrl: orderLogoUrl,
    setLogoUrl: setOrderLogoUrl,
  } = useOrder();
  const cart = orderData?.items || [];
  const [resetSelections, setResetSelections] = useState(0);
  const [selectedchoice, setSelectedchoice] = useState<
    "veg" | "non-veg" | null
  >(null);
  const [stickyStates, setStickyStates] = useState<{ [key: string]: boolean }>(
    {},
  );
  const [isOpen, setIsOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    cartId?: string;
    itemName?: string;
    dealId?: string;
    isDeal?: boolean;
    itemCount?: number; // Add this line
  }>({
    open: false,
  });
  const [customSelections, setCustomSelections] =
    useState<CustomSelectionsWithSections>({});

  const dropdownRef = useRef(null);
  // Holds preselections to apply in the next customize modal open (bundle slots)
  const pendingPreselectionsRef = useRef<Record<string, number>>({});

  const handleChange = (e: any) => {
    const value = e.target.value;
    if (value === "" || (Number(value) >= 1 && Number(value) <= 20)) {
      setTableToken(value);
    }
  };
  // Function to create URL-friendly slug from item name
  const createSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple hyphens with single
      .trim();
  };
  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        dropdownRef.current &&
        !(dropdownRef.current as HTMLElement).contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    if (orderData?.appliedDeals) {
      setAppliedDeals(orderData?.appliedDeals);
    }
  }, [orderData?.appliedDeals]);

  const customizationGroupRefs = useRef<Record<string, HTMLDivElement | null>>(
    {},
  );
  const pendingScrollGroupIndexRef = useRef<number | null>(null);
  const handleChoiceChange = (key: string, newQty: number) => {
    setCustomSelections((prev) => {
      const nextQty = Math.max(newQty, -1);
      const nextSelections = { ...prev, [key]: nextQty };
      if (!selectedCustomizeItem) return nextSelections;

      const [groupName, choiceLabel] = key.split("|||");
      const groupIndex = selectedCustomizeItem.customize.findIndex(
        (group) => group.name === groupName,
      );
      if (groupIndex < 0) return nextSelections;

      const group = selectedCustomizeItem.customize[groupIndex];
      const choice = group.choices.find((c) => c.label === choiceLabel);
      const maxToppings = group.maxToppingsAllowed ?? 0;
      if (!choice || choice.default || maxToppings <= 0) return nextSelections;

      const getGroupTotal = (selections: CustomSelectionsWithSections) =>
        group.choices.reduce((sum, c) => {
          if (c.default) return sum;
          return (
            sum + Math.max(0, Number(selections[`${group.name}|||${c.label}`] || 0))
          );
        }, 0);

      const prevTotal = getGroupTotal(prev);
      const nextTotal = getGroupTotal(nextSelections);
      if (
        prevTotal < maxToppings &&
        nextTotal >= maxToppings &&
        groupIndex < selectedCustomizeItem.customize.length - 1
      ) {
        pendingScrollGroupIndexRef.current = groupIndex + 1;
      }

      return nextSelections;
    });
  };

  const hasRequiredSelection = useCallback(
    (group: CustomizeGroup) => {
      return group.choices.some((choice) => {
        const key = `${group.name}|||${choice.label}`;
        const qty = Number(customSelections[key] || 0);
        const quantityOptions =
          group.quantityOptions?.length ? group.quantityOptions : [0, 1, 2, 3];
        const isDefaultWithOffset = !!choice.default && quantityOptions[0] < 0;

        return isDefaultWithOffset ? qty >= 0 : qty > 0;
      });
    },
    [customSelections],
  );

  const firstMissingRequiredGroupIndex = useMemo(() => {
    if (!selectedCustomizeItem) return -1;

    return selectedCustomizeItem.customize.findIndex((group) => {
      if (group.required && !hasRequiredSelection(group)) return true;
      const minAddons = group.minAddons ?? 0;
      if (minAddons > 0) {
        const totalSelected = group.choices.reduce((sum, c) => {
          if (c.default) return sum;
          return sum + Math.max(0, Number(customSelections[`${group.name}|||${c.label}`] || 0));
        }, 0);
        if (totalSelected < minAddons) return true;
      }
      return false;
    });
  }, [selectedCustomizeItem, hasRequiredSelection, customSelections]);

  const isCustomizationValid = firstMissingRequiredGroupIndex === -1;

  const focusFirstMissingRequiredGroup = useCallback(() => {
    if (firstMissingRequiredGroupIndex < 0) return;
    const refKey = `group-${firstMissingRequiredGroupIndex}`;
    const el = customizationGroupRefs.current[refKey];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [firstMissingRequiredGroupIndex]);
  useEffect(() => {
    const pendingGroupIndex = pendingScrollGroupIndexRef.current;
    if (pendingGroupIndex === null) return;
    pendingScrollGroupIndexRef.current = null;

    const nextGroupEl =
      customizationGroupRefs.current[`group-${pendingGroupIndex}`];
    if (nextGroupEl) {
      nextGroupEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [customSelections]);
  // Function to create URL-friendly slug from item name
const shouldShowPortionSelector = (
  isHalfHalfPizza: boolean,
  group: CustomizeGroup,
) => {
  return isHalfHalfPizza && !!group.showPortionSelector;
};
  const handleHalfHalfSelection = (
    choiceKey: string,
    section: "left" | "right" | "whole",
  ) => {
    const currentSection =
      (customSelections[`${choiceKey}_section`] as string) || "";
    const currentQuantity = (customSelections[choiceKey] as number) || 0;
    const [groupName, choiceLabel] = choiceKey.split("|||");
    const group = selectedCustomizeItem?.customize.find(
      (g) => g.name === groupName,
    );
    const choice = group?.choices.find((c) => c.label === choiceLabel);
    const quantityOptions = group?.quantityOptions?.length
      ? group.quantityOptions
      : [0, 1, 2, 3];
    const hasDefaultOffset = !!choice?.default && quantityOptions[0] < 0;
    const minQuantityForSection = hasDefaultOffset ? 0 : 1;
    let newSection: string = "";

    if (currentSection === section) {
      // If clicking the same section, deselect it
      newSection = "";
    } else {
      // If clicking a different section, select it directly
      newSection = section;
    }

    setCustomSelections((prev) => ({
      ...prev,
      [`${choiceKey}_section`]: newSection,
      [choiceKey]: newSection
        ? Math.max(currentQuantity, minQuantityForSection)
        : currentQuantity,
    }));
  };

  useEffect(() => {
    console.log("dbhjdgd", customSelections);
  }, [customSelections]);

  const getLogo = async () => {
    if (orderLogoUrl) {
      setLogoUrl(orderLogoUrl);
      setIsLogoLoaded(true);
      return;
    }
    const logo = await getPlatformLogo();
    if (logo.data.success && logo.data.data?.logo) {
      console.log("Fetched platform logo:", logo.data.data.logo);
      setIsLogoLoaded(false);
      setLogoUrl(logo.data.data.logo);
    } else {
      setLogoUrl("");
      setIsLogoLoaded(true);
    }
  };
  useEffect(() => {
    getLogo();
  }, [orderLogoUrl]); 
  useEffect(() => {
    if (!logoUrl) return;
    if (logoUrl !== orderLogoUrl) {
      setOrderLogoUrl(logoUrl);
    }
  }, [logoUrl, orderLogoUrl, setOrderLogoUrl]);

  useEffect(() => {
    const check = () => setIsDrawer(window.innerWidth <= 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  const fetchHotelInfo = async (restaurantIdForTax: string) => {
    if (!restaurantIdForTax) return;
    try {
      const { data } = await api.get<ApiResponse<{ taxes: [TaxesProps] }>>(
        `/admin/getTaxInfo/${restaurantIdForTax}`,
      );
      if (data.success && data.data) {
        // You can store taxRate in state or context if needed
        console.log("Fetched hotel tax rate:", data.data.taxes);
        setTaxes(data.data.taxes);
      }
    } catch (error) {
      console.error("Failed to fetch hotel info", error);
    }
  };
  const fetchCodes = async () => {
    const res = await getCodes();
    setCategoryCodes(res.data);
  };
  useEffect(() => {
    //on load of App get hotel address from env and then call API to get Tax information
    fetchCodes();
  }, []);

  useEffect(() => {
    fetchHotelInfo(restaurantId);
    console.log(restaurantId)
  }, [restaurantId]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const itemParam = urlParams.get("item");
    console.log("Checking URL for item param:", itemParam);

    if (itemParam && menuItems.length > 0) {
      // Find the item that matches the URL slug
      const foundItem = menuItems.find((item) => {
        const itemSlug = createSlug(item.name);
        return itemSlug === itemParam;
      });

      if (foundItem) {
        setSelectedCustomizeItem(foundItem);
        setCustomizingItem(null);
        setShowcustModal(true);
      }
    }
  }, [menuItems]);
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);

      const itemParam = urlParams.get("item");
      console.log("Popstate detected, item param:", urlParams);

      if (itemParam && menuItems.length > 0) {
        const foundItem = menuItems.find((item) => {
          const itemSlug = createSlug(item.name);
          return itemSlug === itemParam;
        });

        if (foundItem) {
          setSelectedCustomizeItem(foundItem);
          setCustomizingItem(null);
          setShowcustModal(true);
        }
      } else {
        // No item param, close modal if open
        console.log("Closing modal due to popstate");
        setShowcustModal(false);
        setSelectedCustomizeItem(null);
        setCustomSelections({});
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [menuItems]);
  // CATEGORY SCROLLER
  const menuRef = useRef<HTMLDivElement>(null);

  const isManualScrolling = useRef(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

  // Category names an item is listed under: its primary category plus any additional ones
  const getItemCategoryNames = useCallback(
    (item: MenuItem): string[] => {
      const names = new Set<string>();
      if (item.category && item.category.trim() !== "") names.add(item.category);
      (item.additionalCategoryIds || []).forEach((id) => {
        const cat = categories.find((c) => c._id === id);
        if (cat) names.add(cat.name);
      });
      return Array.from(names);
    },
    [categories],
  );

  // Group menu items by category
  const groupedMenuItems = useMemo(() => {
    const grouped: { [key: string]: MenuItem[] } = {};

    menuItems.forEach((item) => {
      getItemCategoryNames(item).forEach((categoryName) => {
        if (!grouped[categoryName]) {
          grouped[categoryName] = [];
        }
        grouped[categoryName].push(item);
      });
    });

    // Sort by category order
    const sortedGrouped: { [key: string]: MenuItem[] } = {};
    categories.forEach((cat) => {
      if (grouped[cat.name]) {
        sortedGrouped[cat.name] = grouped[cat.name];
      }
    });

    // Add any remaining categories not in the main list
    Object.keys(grouped).forEach((cat) => {
      if (!sortedGrouped[cat]) {
        sortedGrouped[cat] = grouped[cat];
      }
    });

    // Deals: on top of (or after) the categories they're set for; the rest in a
    // "Deals" section first. Only deals whose every group has an item here.
    const available = new Set(menuItems.map((mi) => mi._id));
    const usable = deals.filter(
      (d) =>
        (d.bundleSlots || []).length > 0 &&
        (d.bundleSlots || []).every((sl: any) => (sl.options || []).some((o: any) => available.has(String(o?._id ?? o)))),
    );
    if (usable.length) {
      const toCard = (d: any) =>
        ({
          _id: `deal:${d._id}`,
          name: d.name,
          price: Number(d.bundlePrice) || 0,
          imageUrl: d.imageUrl,
          description: d.description,
          foodType: "both",
          customize: [],
          isDeal: true,
          deal: d,
          priceLabel:
            d.pricingMode === "cheapest_free"
              ? "Buy 1 Get 1 Free"
              : d.pricingMode === "percent_off"
                ? `${d.discountPercent}% off`
                : undefined,
        }) as unknown as MenuItem;
      const loose: MenuItem[] = [];
      for (const d of usable) {
        const names: string[] = (d.categoryNames || []).filter((n: string) => sortedGrouped[n]);
        if (!names.length) loose.push(toCard(d));
        for (const n of names) {
          sortedGrouped[n] = d.showAtTop === false ? [...sortedGrouped[n], toCard(d)] : [toCard(d), ...sortedGrouped[n]];
        }
      }
      if (loose.length) return { Deals: loose, ...sortedGrouped };
    }

    return sortedGrouped;
  }, [menuItems, categories, getItemCategoryNames, deals]);

  // Sidebar / category bar: a "Deals" entry first when deals have their own section
  const navCategories = useMemo(
    () =>
      groupedMenuItems["Deals"] && !categories.some((c) => c.name === "Deals")
        ? [{ _id: "deals", name: "Deals" } as (typeof categories)[number], ...categories]
        : categories,
    [groupedMenuItems, categories],
  );

  // Intersection Observer to track which category section is in view
  useEffect(() => {
    if (!menuRef.current) return;

    const observerOptions = {
      root: menuRef.current,
      rootMargin: "-80px 0px -60% 0px", // Account for sticky header height
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      if (isManualScrolling.current) return;

      // Find the entry with the highest intersection ratio
      let mostVisible = {
        entry: null as IntersectionObserverEntry | null,
        ratio: 0,
      };

      entries.forEach((entry) => {
        if (
          entry.isIntersecting &&
          entry.intersectionRatio > mostVisible.ratio
        ) {
          mostVisible = { entry, ratio: entry.intersectionRatio };
        }
      });

      if (mostVisible.entry) {
        const categoryName = mostVisible.entry.target.getAttribute(
          "data-category-section",
        );
        if (categoryName) {
          setSelectedCategory(categoryName);
        }
      }
    }, observerOptions);

    Object.values(categorySectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [groupedMenuItems]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const categoryName = entry.target.getAttribute(
            "data-category-header",
          );
          if (categoryName) {
            setStickyStates((prev) => ({
              ...prev,
              [categoryName]: !entry.isIntersecting,
            }));
          }
        });
      },
      {
        threshold: [1],
        rootMargin: "-1px 0px 0px 0px",
      },
    );

    // Observe all category headers
    Object.values(categoryHeaderRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [groupedMenuItems]);
  // Function to handle category selection - scrolls to show header AND first item
  const handleCategorySelect = (categoryName: string) => {
    const sectionElement = categorySectionRefs.current[categoryName];

    if (sectionElement && menuRef.current) {
      // Get the position we need to scroll to
      const menuTop = menuRef.current.getBoundingClientRect().top;
      const sectionTop = sectionElement.getBoundingClientRect().top;

      const scrollTop = sectionTop - menuTop + menuRef.current.scrollTop;

      // Pause observer updates
      isManualScrolling.current = true;

      menuRef.current.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });

      // Clear any existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      // When scroll stops, allow observer again
      const handleScrollEnd = () => {
        if (scrollTimeout.current) {
          clearTimeout(scrollTimeout.current);
        }
        scrollTimeout.current = setTimeout(() => {
          isManualScrolling.current = false;
        }, 150);
      };

      menuRef.current.addEventListener("scroll", handleScrollEnd, {
        once: false,
      });

      // Safety timeout
      setTimeout(() => {
        menuRef.current?.removeEventListener("scroll", handleScrollEnd);
        isManualScrolling.current = false;
      }, 1200);
    }

    setSelectedCategory(categoryName);
  };

  // Lock body scroll when drawer opens (phones + tablets)
  useEffect(() => {
    if (!isDrawer) return;
    const { style } = document.body;
    const prevOverflow = style.overflow;
    const prevPaddingRight = style.paddingRight;

    if (isCartOpen) {
      const sw = window.innerWidth - document.documentElement.clientWidth;
      style.overflow = "hidden";
      if (sw > 0) style.paddingRight = `${sw}px`;
    } else {
      style.overflow = "";
      style.paddingRight = "";
    }
    return () => {
      style.overflow = prevOverflow;
      style.paddingRight = prevPaddingRight;
    };
  }, [isDrawer, isCartOpen]);

  // modal radiobuttons
useEffect(() => {
  if (!selectedCustomizeItem || customizingItem) return;

  const defaults: Record<string, number> = {};
  selectedCustomizeItem.customize.forEach((group: any) => {
    const quantityOptions = group.quantityOptions?.length
      ? group.quantityOptions
      : [0, 1, 2, 3];
    const hasDefaultOffset = quantityOptions[0] < 0;

    group.choices
      .filter((c: any) => c.default)
      .forEach((defaultChoice: any) => {
        const key = `${group.name}|||${defaultChoice.label}`;
        defaults[key] = hasDefaultOffset ? 0 : 1;
      });
  });

  // Merge with any bundle slot preselections (overrides defaults, e.g. XL size)
  const pending = pendingPreselectionsRef.current;
  pendingPreselectionsRef.current = {}; // clear after consuming

  if (Object.keys(pending).length > 0) {
    // For every group that has a preselection, zero out its defaults so the
    // radio group never ends up with two choices both at qty=1 simultaneously.
    const pendingGroups = new Set(Object.keys(pending).map((k) => k.split("|||")[0]));
    Object.keys(defaults).forEach((key) => {
      if (pendingGroups.has(key.split("|||")[0])) defaults[key] = 0;
    });
  }

  setCustomSelections({ ...defaults, ...pending });
}, [selectedCustomizeItem, resetSelections]);


  // Keys locked by bundle preselections — user cannot change them and their price is not added
  const lockedKeys = useMemo(() => {
    if (!activeBundleDeal) return new Set<string>();
    const slot = activeBundleDeal.slots[activeBundleDeal.currentSlotIndex];
    return new Set((slot?.preselections || []).map((p) => `${p.groupName}|||${p.choiceLabel}`));
  }, [activeBundleDeal]);

  const loyaltyDiscountAmount = useMemo(() => {
    const nonDealSubtotal = cart.reduce((total, item) => {
      if (!item.isPartOfDeal) {
        return total + item.price * item.quantity;
      }
      return total;
    }, 0);
    return (nonDealSubtotal * (discount ?? 0)) / 100;
  }, [cart, discount]);

  // modal remember choice
  const buildSelectionsFromOptions = (
    menuItem: MenuItem | undefined,
    options: { label: string; price: number; qty: number; groupName?: string }[] = [],
  ) => {
    if (!menuItem || !Array.isArray(options)) return {};
    const map: Record<string, number | string> = {};
    const labelUseCount: Record<string, number> = {};

     const isHalfHalfPizza = menuItem?.category
                  ?.toLowerCase()
                  .includes("pizza")
                  || menuItem.customize.some((grp) => grp.portionOptions);

    options.forEach((opt) => {
      let choiceLabel = opt.label;
      let section = "";

      // For Half & Half Pizza, extract section from label
      if (isHalfHalfPizza) {
        const sections = ["left", "right", "whole"];
        const foundSection = sections.find((s) =>
          opt.label.toLowerCase().endsWith(` ${s}`),
        );

        if (foundSection) {
          section = foundSection;
          choiceLabel = opt.label
            .replace(new RegExp(` ${foundSection}$`, "i"), "")
            .trim();
        }
      }

      let group =
        opt.groupName &&
        menuItem.customize.find(
          (g) =>
            g.name === opt.groupName &&
            g.choices.some((c) => c.label === choiceLabel),
        );

      if (!group) {
        const candidateGroups = menuItem.customize.filter((g) =>
          g.choices.some((c) => c.label === choiceLabel),
        );

        if (candidateGroups.length > 0) {
          const used = labelUseCount[choiceLabel] || 0;
          group = candidateGroups[Math.min(used, candidateGroups.length - 1)];
          labelUseCount[choiceLabel] = used + 1;
        }
      }

      if (group) {
        const key = `${group.name}|||${choiceLabel}`;
        map[key] = opt.qty;

        // Set section for Half & Half Pizza
        if (isHalfHalfPizza && section) {
          map[`${key}_section`] = section;
        }
      }
    });

    return map;
  };

  // Close on Escape
  useEffect(() => {
    if (!isCartOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsCartOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isCartOpen]);

  // Close drawer when leaving drawer breakpoint
  useEffect(() => {
    if (!isDrawer && isCartOpen) setIsCartOpen(false);
  }, [isDrawer, isCartOpen]);

  const toggleCart = () => setIsCartOpen((v) => !v);
  const closeCart = () => setIsCartOpen(false);

  // Memo totals
  const totalItems = useMemo(
    () => cart.reduce((t, i) => t + i.quantity, 0),
    [cart],
  );
  const subtotal = useMemo(() => {
    return cart.reduce((total, item) => {
      // Regular items
      if (!item.isPartOfDeal) {
        return total + item.price * item.quantity;
      }
      // Deal items use originalPrice for subtotal display
      return total + (item.originalPrice || item.price) * item.quantity;
    }, 0);
  }, [cart]);

  const totalAmount = useMemo(
    () => cart.reduce((t, i) => t + (i.price + unitTax(i)) * i.quantity, 0),
    [cart],
  );
  const totalTax = useMemo(
    () => cart.reduce((total, item) => total + unitTax(item) * item.quantity, 0),
    [cart],
  );
  const totalGst = useMemo(
    () => cart.reduce((total, item) => total + (item.gst || 0) * item.quantity, 0),
    [cart],
  );
  const totalPst = useMemo(
    () => cart.reduce((total, item) => total + (item.pst || 0) * item.quantity, 0),
    [cart],
  );

  const discountAmount = useMemo(
    () => (subtotal * (discount ?? 0)) / 100,
    [subtotal, discount],
  );

  const getDisplayAddedOptions = (cartItem: any) => {
  const options = cartItem?.customOptions || [];
  if (!Array.isArray(options) || options.length === 0) return [];

  const menuItem = menuItems.find((m) => m._id === cartItem.itemId);

  return options
    .map((opt: any) => {
      const rawQty = Number(opt?.qty || 0);
      if (rawQty <= 0) return null;

      const baseLabel = String(opt?.label || "")
        .replace(/\s+(left|right|whole)$/i, "")
        .trim();

      const matchedChoice = menuItem?.customize
        ?.flatMap((g) => g.choices || [])
        ?.find((c) => c.label === baseLabel);

      const isDefaultChoice = Boolean(matchedChoice?.default);
      const displayQty = isDefaultChoice ? Math.max(0, rawQty - 1) : rawQty;

      if (displayQty <= 0) return null;

      return {
        label: String(opt?.label || ""),
        qty: displayQty,
      };
    })
    .filter((x): x is { label: string; qty: number } => x !== null);
};

  const getDisplayRemovedOptions = (cartItem: any) => {
    const options = cartItem?.customOptions || [];
    if (!Array.isArray(options) || options.length === 0) return [];

    return options
      .map((opt: any) => {
        const rawQty = Number(opt?.qty || 0);
        if (rawQty >= 0) return null;

        return {
          label: String(opt?.label || ""),
          qty: Math.abs(rawQty),
        };
      })
      .filter((x): x is { label: string; qty: number } => x !== null);
  };


  const removeDealFromCart = (dealId: string) => {
    const newCart = cart.filter((item) => item.dealId !== dealId);
    const newDeals = appliedDeals.filter((deal) => deal.dealId !== dealId);

    setOrderData((prev) => ({
      ...prev,
      items: newCart,
      appliedDeals: newDeals,
    }));

    toast.success("Deal removed from cart");
  };

  // Silently cancel an in-progress BYO deal — removes partially added items + deal entry
  const cancelBYODeal = (claimId: string) => {
    setOrderData((prev) => ({
      ...prev,
      items: (prev?.items || []).filter((item) => item.dealId !== claimId),
      appliedDeals: (prev?.appliedDeals || []).filter((d) => d.dealId !== claimId),
    }));
    setActiveBYODeal(null);
    setByoPicker(null);
  };

  // Silently cancel an in-progress bundle deal — removes fixed items already added + deal entry
  const cancelBundleDeal = (claimId: string) => {
    setOrderData((prev) => ({
      ...prev,
      items: (prev?.items || []).filter((item) => item.dealId !== claimId),
      appliedDeals: (prev?.appliedDeals || []).filter((d) => d.dealId !== claimId),
    }));
    setActiveBundleDeal(null);
  };
  const totalDealDiscount = useMemo(() => {
    return appliedDeals.reduce((sum, deal) => sum + deal.discountAmount, 0);
  }, [appliedDeals]);
  const grandTotal = useMemo(() => {
    let total = subtotal;

    // Subtract deal discounts (if not already in prices)
    total -= totalDealDiscount;

    // Subtract loyalty discount (only on non-deal items)
    total -= loyaltyDiscountAmount;

    // Add taxes
    total += totalTax;

    return Math.max(0, total); // Ensure non-negative
  }, [subtotal, totalDealDiscount, loyaltyDiscountAmount, totalTax]);

  // modal data pass
const handleCustomSubmit = () => {
  if (!selectedCustomizeItem) return;
            const isHalfHalfPizza = selectedCustomizeItem.category
                  ?.toLowerCase()
                  .includes("pizza")
                  || selectedCustomizeItem.customize.some((grp) => grp.portionOptions);

  let extraPrice = 0;

  const selectedOptions = selectedCustomizeItem.customize.flatMap((group) =>
    group.choices
      .map((choice) => {
        const key = `${group.name}|||${choice.label}`;
        const qty = (customSelections[key] as number) || 0;
        const section = customSelections[`${key}_section`] as string;

        if (qty === -1 || qty > 0) {
          const label =
            isHalfHalfPizza && section
              ? `${choice.label} ${section}`
              : choice.label;

          const unitPrice = choice.price || 0;
          const quantityOptions = group.quantityOptions?.length
            ? group.quantityOptions
            : [0, 1, 2, 3];
          const hasDefaultOffset = choice.default && quantityOptions[0] < 0;
          const chargeableQty = choice.default
            ? hasDefaultOffset
              ? Math.max(0, qty)
              : Math.max(0, qty - 1)
            : Math.max(0, qty);

          extraPrice += unitPrice * chargeableQty;

          return {
            label,
            price: unitPrice,
            qty,
            groupName: group.name,
            displayType: group.displayType || '',
          };
        }
        return null;
      })
      .filter(
        (c): c is { label: string; price: number; qty: number; groupName: string; displayType: string } =>
          c !== null,
      ),
  );

  // Apply free toppings discount
  const freeToppingDiscount = computeFreeToppingDiscount(
    selectedCustomizeItem,
    customSelections,
  );
  extraPrice = Math.max(0, extraPrice - freeToppingDiscount);

  let newCart: OrderItem[];
  let tax = 0;
let gstRate = 0;
let pstRate = 0;
  if (categoryCodes) {
    // Use item's own categoryCode override if set, else fall back to category's code
    let effectiveCategoryCodeId = selectedCustomizeItem.categoryCode;
    if (!effectiveCategoryCodeId) {
      const filteredCode = categories.filter((item) => item._id == selectedCustomizeItem.categoryId);
      if (filteredCode.length > 0) {
        effectiveCategoryCodeId = filteredCode[0].categoryCode;
      }
    }
    if (effectiveCategoryCodeId) {
      const categoryCodeFilter = categoryCodes.filter((item) => item._id == effectiveCategoryCodeId);
      if (categoryCodeFilter.length > 0) {
        const taxItem = taxes?.filter((item) => item.category_code == categoryCodeFilter[0].code);
        if (taxItem && taxItem.length > 0) {
          const t = taxItem[0];
          tax = t.gst != null || t.pst != null
            ? Number(t.gst ?? 0) + Number(t.pst ?? 0)
            : Number(t.tax_percent ?? 0);
          gstRate = Number(t.gst ?? 0);
          pstRate = Number(t.pst ?? 0);
        }
      }
    }
  }

  if (activeBundleDeal) {
    // ── BUNDLE mode ── each slot item customized; extras charged on top of bundlePrice
    const pizzaFinalPrice = selectedCustomizeItem.price + extraPrice;

    const bundleCartItem: OrderItem = {
      itemId: selectedCustomizeItem._id,
      name: selectedCustomizeItem.name,
      kitchenName: selectedCustomizeItem.kitchenName,
      price: pizzaFinalPrice,
      categoryId: selectedCustomizeItem.categoryId,
      tax: Number(((pizzaFinalPrice * tax) / 100).toFixed(2)),
      cartId: Date.now().toString() + Math.random().toString(36).substring(2, 9),
      quantity: 1,
      imageUrl: selectedCustomizeItem.imageUrl,
      customOptions: selectedOptions,
      dealId: activeBundleDeal.claimId,
      dealName: activeBundleDeal.dealName,
      isPartOfDeal: true,
    };

    newCart = [...cart, bundleCartItem];

    // Discount covers base price + locked (preselected) extras only.
    // Freely chosen upsells are charged on top of bundlePrice.
    const lockedExtrasCost = Array.from(lockedKeys).reduce((sum, key) => {
      const [groupName, choiceLabel] = key.split("|||");
      const grp = selectedCustomizeItem.customize.find((g) => g.name === groupName);
      const ch = grp?.choices.find((c) => c.label === choiceLabel);
      return sum + (ch?.price || 0);
    }, 0);
    const newBasePricesSum = activeBundleDeal.pizzaBasePricesSum + selectedCustomizeItem.price + lockedExtrasCost;
    const newBasePrices = [...activeBundleDeal.basePrices, selectedCustomizeItem.price + lockedExtrasCost];
    const slotUpcharge =
      activeBundleDeal.slots[activeBundleDeal.currentSlotIndex]?.upcharges?.find(
        (u) => u.itemId === selectedCustomizeItem._id,
      )?.amount || 0;
    const newUpchargeTotal = activeBundleDeal.upchargeTotal + slotUpcharge;
    const isLastSlot = activeBundleDeal.currentSlotIndex === activeBundleDeal.slots.length - 1;

    setResetSelections((c) => c + 1);
    setCustomSelections({});

    if (isLastSlot) {
      // All slots filled — apply bundle pricing.
      // Can be positive (items > bundlePrice → discount) OR negative (items < bundlePrice → surcharge).
      // Either way, grandTotal = bundlePrice + extras.
      // Discount by the deal's price rule; extra charges for particular items
      // reduce it, and it never goes below 0 (never more than the items cost)
      const itemsTotal = newBasePricesSum + activeBundleDeal.fixedItemsTotal;
      const rawDiscount =
        activeBundleDeal.pricingMode === "cheapest_free"
          ? newBasePrices.length > 1
            ? Math.min(...newBasePrices)
            : 0
          : activeBundleDeal.pricingMode === "percent_off"
            ? (itemsTotal * activeBundleDeal.discountPercent) / 100
            : itemsTotal - activeBundleDeal.bundlePrice;
      const finalDiscount = Math.min(itemsTotal, Math.max(0, rawDiscount - newUpchargeTotal));
      setShowcustModal(false);
      setOrderData((prev) => ({
        ...prev,
        items: newCart,
        appliedDeals: (prev?.appliedDeals || []).map((d) =>
          d.dealId === activeBundleDeal.claimId
            ? { ...d, discountAmount: Number(finalDiscount.toFixed(2)) }
            : d,
        ),
      }));
      setActiveBundleDeal(null);
    } else {
      // More slots remain — advance to next slot
      const nextSlotIndex = activeBundleDeal.currentSlotIndex + 1;
      const nextSlot = activeBundleDeal.slots[nextSlotIndex];
      const nextItem = menuItems.find((m) => m._id === nextSlot.options[0]);

      // Build preselections for next slot
      const nextPreselections = nextSlot.preselections || [];
      const nextPreCustom: Record<string, number> = {};
      nextPreselections.forEach(({ groupName, choiceLabel }) => {
        nextPreCustom[`${groupName}|||${choiceLabel}`] = 1;
      });

      // Save cart progress and advance slot index
      setOrderData((prev) => ({ ...prev, items: newCart }));
      setActiveBundleDeal((prev) =>
        prev
          ? {
              ...prev,
              currentSlotIndex: nextSlotIndex,
              pizzaBasePricesSum: newBasePricesSum,
              basePrices: newBasePrices,
              upchargeTotal: newUpchargeTotal,
            }
          : null
      );

      if (nextItem) {
        setShowcustModal(false);
        setTimeout(() => {
          // Set preselections INSIDE the timeout so the ref isn't consumed
          // by the early setResetSelections call above before the next item opens
          if (nextPreselections.length > 0) {
            pendingPreselectionsRef.current = nextPreCustom;
          }
          setSelectedCustomizeItem(nextItem);
          setResetSelections((c) => c + 1);
          setShowcustModal(true);
        }, 120);
      } else {
        // Next item not in menu — roll back entire claim
        cancelBundleDeal(activeBundleDeal.claimId);
        toast.error("Bundle item not found. Please try again.");
      }
    }
    return;
  }

  if (activeBYODeal) {
    // ── BUILD YOUR OWN mode ── (always takes priority over customizingItem)
    // First `includedToppings` selected options (qty > 0) are free; the rest keep their menu price.
    const included = activeBYODeal.includedToppings;
    let byoExtraPrice = 0;

    // Calculate extras at full price first (same as normal item flow)
    const byoOptions = selectedOptions.map((opt) => {
      const chargeableQty = Math.max(0, opt.qty);
      byoExtraPrice += (opt.price || 0) * chargeableQty;
      return opt;
    });

    // Then apply free topping discount — same logic as computeFreeToppingDiscount
    // but using activeBYODeal.includedToppings as the free count
    let freeRemaining = included;
    let freeToppingDiscount = 0;
    for (const group of selectedCustomizeItem.customize) {
      if (group.required || group.multiple || freeRemaining <= 0) continue;
      for (const choice of group.choices) {
        if (freeRemaining <= 0) break;
        if (choice.default) continue;
        const key = `${group.name}|||${choice.label}`;
        const qty = Number((customSelections as Record<string, number>)[key] || 0);
        if (qty > 0) {
          const freeQty = Math.min(qty, freeRemaining);
          freeToppingDiscount += (choice.price || 0) * freeQty;
          freeRemaining -= freeQty;
        }
      }
    }
    byoExtraPrice = Math.max(0, byoExtraPrice - freeToppingDiscount);

    const finalPrice = activeBYODeal.basePrice + byoExtraPrice;

    const byoCartItem: OrderItem = {
      itemId: selectedCustomizeItem._id,
      name: selectedCustomizeItem.name,
      kitchenName: selectedCustomizeItem.kitchenName,
      price: finalPrice,
      categoryId: selectedCustomizeItem.categoryId,
      tax: Number(((finalPrice * tax) / 100).toFixed(2)),
      cartId: Date.now().toString() + Math.random().toString(36).substring(2, 9),
      quantity: 1,
      imageUrl: selectedCustomizeItem.imageUrl,
      customOptions: byoOptions,
      dealId: activeBYODeal.claimId,   // tag with claimId, not deal._id
      isPartOfDeal: true,
    };

    newCart = [...cart, byoCartItem];

    const nextPizzasDone = activeBYODeal.pizzasDone + 1;

    setResetSelections((c) => c + 1);
    setOrderData((prev) => ({ ...prev, items: newCart }));
    setCustomSelections({});
    setShowcustModal(false);

    if (nextPizzasDone < activeBYODeal.minPizzas) {
      // More pizzas to configure for this deal
      setActiveBYODeal({ ...activeBYODeal, pizzasDone: nextPizzasDone });
      if (activeBYODeal.itemIds.length > 1) {
        // Multiple eligible items — show picker for next pizza
        setTimeout(() => setByoPicker({ pizzaIndex: nextPizzasDone }), 300);
      } else {
        // Single item deal — re-open customize for same item
        setTimeout(() => {
          const sameItem = menuItems.find(m => m._id === activeBYODeal.itemIds[0]) ?? selectedCustomizeItem;
          setSelectedCustomizeItem(sameItem);
          setShowcustModal(true);
        }, 300);
      }
    } else {
      // All pizzas done
      setActiveBYODeal(null);
    }
    return;
  }

  if (customizingItem) {
    if (editingDealMeta) {
      // Editing a deal item — repricing depends on deal type
      let newPrice: number;
      let newTax: number;
      if (
        editingDealMeta.dealType === 'build_your_own' &&
        editingDealMeta.byoBasePrice !== undefined
      ) {
        // BYO: basePrice + extra toppings - free toppings discount
        const included = editingDealMeta.byoIncludedToppings || 0;
        let byoExtraPrice = 0;
        for (const opt of selectedOptions) {
          byoExtraPrice += (opt.price || 0) * Math.max(0, opt.qty);
        }
        // Apply BYO free toppings discount
        let freeRemaining = included;
        let freeToppingDiscount = 0;
        for (const group of selectedCustomizeItem.customize) {
          if (group.required || group.multiple || freeRemaining <= 0) continue;
          for (const choice of group.choices) {
            if (freeRemaining <= 0) break;
            if (choice.default) continue;
            const key = `${group.name}|||${choice.label}`;
            const qty = Number((customSelections as Record<string, number>)[key] || 0);
            if (qty > 0) {
              const freeQty = Math.min(qty, freeRemaining);
              freeToppingDiscount += (choice.price || 0) * freeQty;
              freeRemaining -= freeQty;
            }
          }
        }
        byoExtraPrice = Math.max(0, byoExtraPrice - freeToppingDiscount);
        newPrice = editingDealMeta.byoBasePrice + byoExtraPrice;
        newTax = Number(((newPrice * tax) / 100).toFixed(2));
      } else if (editingDealMeta.dealType === 'bundle') {
        // Bundle: update pizza price (extras charged on top of bundle price)
        newPrice = selectedCustomizeItem.price + extraPrice;
        newTax = Number(((newPrice * tax) / 100).toFixed(2));
      } else {
        // Standard deals (fixed_price, percentage_discount, buy_x_get_y): keep existing price
        const existingItem = cart.find((c) => c.cartId === customizingItem.cartId);
        newPrice = existingItem?.price ?? selectedCustomizeItem.price;
        newTax = existingItem?.tax ?? 0;
      }
      newCart = cart.map((c) =>
        c.cartId === customizingItem.cartId
          ? { ...c, price: newPrice, customOptions: selectedOptions, tax: newTax }
          : c,
      );
      // For bundle: recalculate discountAmount from updated pizza prices
      if (editingDealMeta.dealType === 'bundle' &&
          editingDealMeta.bundlePrice !== undefined &&
          editingDealMeta.bundleFixedItemsTotal !== undefined) {
        const dealId = customizingItem.dealId;
        const updatedPizzasTotal = newCart
          .filter((c) => c.dealId === dealId && !c.originalPrice)
          .reduce((sum, c) => sum + c.price * c.quantity, 0);
        const newDiscount = updatedPizzasTotal + editingDealMeta.bundleFixedItemsTotal - editingDealMeta.bundlePrice;
        setOrderData((prev) => ({
          ...prev,
          items: newCart,
          appliedDeals: (prev?.appliedDeals || []).map((d) =>
            d.dealId === dealId
              ? { ...d, discountAmount: Number(newDiscount.toFixed(2)) }
              : d,
          ),
        }));
        setResetSelections((c) => c + 1);
        setShowcustModal(false);
        setCustomSelections({});
        setCustomizingItem(null);
        setEditingDealMeta(null);
        return;
      }
    } else {
      newCart = cart.map((c) =>
        c.cartId === customizingItem.cartId
          ? {
              ...c,
              price: selectedCustomizeItem.price + extraPrice,
              customOptions: selectedOptions,
              tax: Number(
                (((selectedCustomizeItem.price + extraPrice) * tax) / 100).toFixed(2),
              ),
              gst: Number(
                (((selectedCustomizeItem.price + extraPrice) * gstRate) / 100).toFixed(2),
              ),
              pst: Number(
                (((selectedCustomizeItem.price + extraPrice) * pstRate) / 100).toFixed(2),
              ),
            }
          : c,
      );
    }
  } else {
    const existing = cart.find(
      (c) =>
        c.itemId === selectedCustomizeItem._id &&
        JSON.stringify(c.customOptions) === JSON.stringify(selectedOptions),
    );

    if (existing) {
      newCart = cart.map((c) =>
        c.cartId === existing.cartId ? { ...c, quantity: c.quantity + 1 } : c,
      );
    } else {
      newCart = [
        ...cart,
        {
          itemId: selectedCustomizeItem._id,
          name: selectedCustomizeItem.name,
          kitchenName: selectedCustomizeItem.kitchenName,
          price: selectedCustomizeItem.price + extraPrice,
          categoryId: selectedCustomizeItem.categoryId,
          tax: Number(
            (((selectedCustomizeItem.price + extraPrice) * tax) / 100).toFixed(2),
          ),
          gst: Number(
            (((selectedCustomizeItem.price + extraPrice) * gstRate) / 100).toFixed(2),
          ),
          pst: Number(
            (((selectedCustomizeItem.price + extraPrice) * pstRate) / 100).toFixed(2),
          ),
          cartId:
            Date.now().toString() + Math.random().toString(36).substring(2, 9),
          quantity: 1,
          imageUrl: selectedCustomizeItem.imageUrl,
          customOptions: selectedOptions,
        },
      ];
    }
  }

  setResetSelections((c) => c + 1);
  setOrderData((prev) => ({ ...prev, items: newCart }));
  setShowcustModal(false);
  setCustomSelections({});
  setCustomizingItem(null);
  setEditingDealMeta(null);
};

  const handleDiscountChange = (newDiscount: number) =>
    setDiscount(newDiscount);

  useEffect(() => {
    fetchMenuItems();
    fetchCategoriesData();
  }, []);

  const fetchMenuItems = async (foodType?: string | null) => {
    try {
      setLoading(true);
      setError(null);

      // Build URL with query parameters
      const params = new URLSearchParams();
      if (foodType) {
        params.append("foodType", foodType);
      }
      // Pass restaurant ID from kitchen context
      if (kitchenUser?.restaurantId) {
        params.append("restaurantId", kitchenUser.restaurantId);
      }
      const url = params.toString() ? `/menu?${params.toString()}` : "/menu";

      const { data } = await api.get<ApiResponse<MenuItem[]>>(url);
      if (data.success && Array.isArray(data.data)) {
        // Hide modifier choices marked unavailable in the admin
        setMenuItems(withoutUnavailableChoices(data.data));
      } else {
        setMenuItems([]);
        setError("Failed to load menu.");
      }
    } catch {
      setMenuItems([]);
      setError("Failed to load menu.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log(`Cart Updated `, cart);
  }, [cart]);

  // Add this useEffect to refetch when selectedchoice or kitchenUser changes
  useEffect(() => {
    fetchMenuItems(selectedchoice);
  }, [selectedchoice, kitchenUser?.restaurantId]);
  // When modal opens, prefill defaults

  const fetchCategoriesData = async () => {
    try {
      const res = await getCategories();
      if (res.data.success) {
        const cats: Category[] = res.data.data.map(
          (cat: {
            name: string;
            imageUrl: string;
            _id: string;
            categoryCode: any;
          }) => ({
            name: cat.name,
            imageUrl: cat.imageUrl,
            _id: cat._id,
            categoryCode: typeof cat.categoryCode === "object" ? cat.categoryCode?._id ?? "" : cat.categoryCode ?? "",
          }),
        );
        setCategories(cats);
        if (!selectedCategory && cats.length > 0) {
          setSelectedCategory(cats[0].name);
        }
      }
    } catch (err) {
      console.error("Failed to fetch categories", err);
    }
  };

  const removeFromCart = (cartId: string) => {
    const item = cart.find((c) => c.cartId === cartId);

    if (item?.isPartOfDeal && item.dealId) {
      // Show confirmation for deal items
      setConfirmModal({
        open: true,
        cartId,
        itemName: item.name,
        dealId: item.dealId,
        isDeal: true,
      });
      return;
    }
    setConfirmModal({
      open: true,
      cartId,
      itemName: item?.name,
      isDeal: false,
    });
  };

  const handleConfirmRemove = () => {
    if (confirmModal.isDeal && confirmModal.dealId) {
      // Remove the entire deal
      removeDealFromCart(confirmModal.dealId);
    } else if (confirmModal.cartId) {
      // Remove single item
      const newCart = cart.filter((c) => c.cartId !== confirmModal.cartId);
      setOrderData((prev) => ({ ...prev, items: newCart }));
    }

    // Close modal
    setConfirmModal({ open: false });
  };
  const updateQuantity = (cartId: string, quantity: number) => {
    const item = cart.find((c) => c.cartId === cartId);

    if (item?.isPartOfDeal) {
      toast.error(
        "Cannot modify quantity of deal items. Remove the entire deal instead.",
      );
      return;
    }

    if (quantity < 1) return removeFromCart(cartId);

    const newCart = cart.map((c) =>
      c.cartId === cartId ? { ...c, quantity } : c,
    );
    setOrderData((prev) => ({ ...prev, items: newCart }));
  };

  const validateDeals = (): boolean => {
    // Check if minimum order amounts are met for each deal
    const nonDealSubtotal = cart.reduce((total, item) => {
      if (!item.isPartOfDeal) {
        return total + item.price * item.quantity;
      }
      return total;
    }, 0);

    // You would need to fetch deal details or store minOrderAmount in appliedDeals
    // For now, we'll assume it's validated when adding the deal

    return true;
  };

  const getItemQty = useCallback(
    (cartId: string) => cart.find((c) => c.cartId === cartId)?.quantity ?? 0,
    [cart],
  );

  const fetchPoints = async (phone: string) => {
    if (!phone) return;
    const cleanPhone = phone.replace(/-/g, "");
    try {
      const { data } = await api.get<ApiResponse<LoyaltyCustomer>>(
        `/loyalty/${cleanPhone}`,
      );
      if (data.success && data.data) setPoints(data.data.points);
      else setPoints(0);
    } catch {
      setPoints(0);
    }
  };
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value;

    // Remove all non-digit and non-dash characters
    const cleaned = input.replace(/[^\d-]/g, "");

    // Extract just the digits for validation and API call
    const digits = cleaned.replace(/-/g, "").slice(0, 10); // Only 10 digits max

    let formatted = "";

    // Check if user manually typed dashes in correct positions only
    const validManualFormat =
      /^\d{1,3}$|^\d{3}-\d{0,3}$|^\d{3}-\d{3}-\d{0,4}$/.test(cleaned);

    if (validManualFormat && cleaned === input.replace(/[^\d-]/g, "")) {
      // User typed valid format with dashes in correct positions, keep it
      formatted = cleaned;
    } else {
      // Auto-format based on digit count
      if (digits.length > 6) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
          6,
        )}`;
      } else if (digits.length > 3) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        formatted = digits;
      }
    }

    setPhoneNumber(formatted);

    // Trigger fetchPoints only if there are exactly 10 digits
    if (digits.length === 10) {
      fetchPoints(digits);
    } else {
      setPoints(0);
      setDiscount(0);
    }
  };

  const handleCheckout = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    if (!phoneNumber) return toast.error("Please enter a phone number.");
    if (!tableToken) return toast.error("Please select your table token.");

    if (phoneNumber.length < 10)
      return toast.error("Phone number must be at least 10 digits.");
    if (typeof tableToken === "number" && (tableToken < 1 || tableToken > 20))
      return toast.error("Table token must be between 1 and 20.");

    // Validate deals
    if (!validateDeals()) {
      return toast.error("Some deals don't meet minimum order requirements.");
    }

    const cleanPhoneNumber = phoneNumber.replace(/-/g, "");
    setLoading(true);

    try {
      const totalString = grandTotal.toFixed(2);
      const totalDiscountString = (
        totalDealDiscount + loyaltyDiscountAmount
      ).toFixed(2);

      setOrderData((prev) => ({
        ...prev,
        items: cart.map(
          ({
            itemId,
            name,
            kitchenName,
            price,
            quantity,
            imageUrl,
            customOptions,
            categoryId,
            tax,
            gst,
            pst,
            dealId,
            dealName,
            isPartOfDeal,
            originalPrice,
          }) => ({
            itemId,
            name,
            kitchenName,
            price,
            quantity,
            categoryId,
            tax: Number(tax ?? 0),
            gst: Number(gst ?? 0),
            pst: Number(pst ?? 0),
            cartId:
              Date.now().toString() +
              Math.random().toString(36).substring(2, 9),
            imageUrl,
            customOptions: customOptions || [],
            dealId,
            dealName,
            isPartOfDeal,
            originalPrice,
          }),
        ),
        restaurantId: kitchenUser?.restaurantId || "",
        totalAmount: totalString,
        discountAmount: totalDiscountString,
        appliedDeals: appliedDeals,
        phoneNumber: cleanPhoneNumber || "",
        tableToken: tableToken || prev?.tableToken || "",
      }));

      navigate(`/pay`);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to place order");
    } finally {
      setLoading(false);
    }
  };

  const categoryOrder = categories.map((cat) => cat.name);
  const categorySectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>(
    {},
  );
  const categoryHeaderRefs = useRef<{ [key: string]: HTMLDivElement | null }>(
    {},
  );

  // Sort menu items by category order
  const sortedMenu = useMemo(() => {
    const categoryOrder = categories.map((cat) => cat.name);

    return [...menuItems].sort((a, b) => {
      const indexA = categoryOrder.indexOf(a.category);
      const indexB = categoryOrder.indexOf(b.category);
      return indexA - indexB;
    });
  }, [menuItems, categories]);

  const filteredMenu = sortedMenu;

  const handleItemClick = (item: MenuItem) => {
    setSelectedItem(item);
    setShowModal(true);
  };
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const itemParam = urlParams.get("item");
    console.log("Checking URL for item param:", itemParam);

    if (itemParam && menuItems.length > 0) {
      // Find the item that matches the URL slug
      const foundItem = menuItems.find((item) => {
        const itemSlug = createSlug(item.name);
        return itemSlug === itemParam;
      });

      if (foundItem) {
        setSelectedCustomizeItem(foundItem);
        setCustomizingItem(null);
        setShowcustModal(true);
      }
    }
  }, [menuItems]);
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);

      const itemParam = urlParams.get("item");
      console.log("Popstate detected, item param:", urlParams);

      if (itemParam && menuItems.length > 0) {
        const foundItem = menuItems.find((item) => {
          const itemSlug = createSlug(item.name);
          return itemSlug === itemParam;
        });

        if (foundItem) {
          setSelectedCustomizeItem(foundItem);
          setCustomizingItem(null);
          setShowcustModal(true);
        }
      } else {
        // No item param, close modal if open
        console.log("Closing modal due to popstate");
        setShowcustModal(false);
        setSelectedCustomizeItem(null);
        setCustomSelections({});
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [menuItems]);
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null);
  };
  const getItemTaxRate = useCallback(
    (menuItem: MenuItem): number => {
      if (!categoryCodes) return 0;

      let effectiveCategoryCodeId = menuItem.categoryCode;
      if (!effectiveCategoryCodeId) {
        const category = categories.find((c) => c._id === menuItem.categoryId);
        effectiveCategoryCodeId = category?.categoryCode;
      }

      if (!effectiveCategoryCodeId) return 0;

      const categoryCode = categoryCodes.find(
        (c) => c._id === effectiveCategoryCodeId,
      );
      if (!categoryCode) return 0;

      const taxItem = taxes?.find(
        (item) => item.category_code === categoryCode.code,
      );
      if (!taxItem) return 0;
      return taxItem.gst != null || taxItem.pst != null
        ? Number(taxItem.gst ?? 0) + Number(taxItem.pst ?? 0)
        : Number(taxItem.tax_percent ?? 0);
    },
    [categories, categoryCodes, taxes],
  );

  // const handleAddToCart = useCallback(
  //   (item: MenuItem) => {
  //     const taxRate = getItemTaxRate(item);
  //     const itemTax = Number(((item.price * taxRate) / 100).toFixed(2));
  //     setOrderData((prev) => {
  //       const prevItems = prev?.items || [];
  //       const existing = prevItems.find(
  //         (c) =>
  //           c.itemId === item._id &&
  //           (!c.customOptions || c.customOptions.length === 0),
  //       );
  //       const newItems = existing
  //         ? prevItems.map((c) =>
  //             c.cartId === existing.cartId
  //               ? { ...c, quantity: c.quantity + 1 }
  //               : c,
  //           )
  //         : [
  //             ...prevItems,
  //             {
  //               itemId: item._id,
  //               name: item.name,
  //               kitchenName: item.kitchenName,
  //               price: item.price,
  //               categoryId: item.categoryId,
  //               tax: itemTax,
  //               cartId:
  //                 Date.now().toString() +
  //                 Math.random().toString(36).substring(2, 9),
  //               quantity: 1,
  //               imageUrl: item.imageUrl,
  //               customOptions: [],
  //             },
  //           ];

  //       return {
  //         ...(prev || {}),
  //         items: newItems,
  //       };
  //     });
  //   },
  //   [getItemTaxRate, setOrderData],
  // );

  const handlecust = (item: MenuItem) => {
    const isCustomizable = item.customize && item.customize.length > 0;
    // If item is NOT customizable, add directly to cart
    // if (!isCustomizable) {
    //   void handleAddToCart(item);
    //   return;
    // }
    setSelectedCustomizeItem(item);
    setCustomizingItem(null);
    setCollapsedGroups({});
    setShowcustModal(true);

    // Update URL with item name
    const itemSlug = createSlug(item.name);
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("item", itemSlug);
    window.history.pushState({}, "", currentUrl.toString());
  };

  // Called when customer claims a Build Your Own deal from DealsModal
  const handleByoDealClaim = (deal: {
    _id: string;
    name: string;
    basePrice?: number;
    includedToppings?: number;
    minPizzas?: number;
    items: { itemId: string | { _id: string } }[];
  }) => {
    const itemIds = deal.items.map(i =>
      typeof i.itemId === 'object' ? i.itemId._id : i.itemId
    );
    if (itemIds.length === 0) return;

    // If minPizzas is explicitly set by admin use it, otherwise default to number of items
    const minPizzas = deal.minPizzas ?? itemIds.length;

    const claimId = Date.now().toString() + Math.random().toString(36).substring(2, 7);

    const byoDealData = {
      claimId,
      dealId: deal._id,
      dealName: deal.name,
      basePrice: deal.basePrice ?? 0,
      includedToppings: deal.includedToppings ?? 0,
      minPizzas,
      pizzasDone: 0,
      itemIds,
    };

    // Clear any stale customizingItem so BYO mode isn't skipped in handleCustomSubmit
    setCustomizingItem(null);
    setActiveBYODeal(byoDealData);

    // Register deal in appliedDeals — use claimId as dealId so multiple claims are independent
    setOrderData((prev) => ({
      ...prev,
      appliedDeals: [
        ...(prev?.appliedDeals || []),
        {
          dealId: claimId,
          originalDealId: deal._id,
          name: deal.name,
          discountAmount: 0,
          dealType: 'build_your_own' as const,
          byoBasePrice: deal.basePrice ?? 0,
          byoIncludedToppings: deal.includedToppings ?? 0,
        },
      ],
    }));

    if (itemIds.length > 1) {
      // Multiple eligible pizzas — show picker so customer can choose
      setByoPicker({ pizzaIndex: 0 });
    } else {
      // Only one option — skip picker, open customize directly
      const baseItem = menuItems.find(m => m._id === itemIds[0]);
      if (baseItem) handlecust(baseItem);
    }
  };

  // Called when customer claims a Bundle deal from DealsModal
  // Start a deal from its menu card: one step per pick, items this location has
  const startDeal = (deal: any) => {
    const idOf = (v: any) => String(v?._id ?? v ?? "");
    const available = new Set(menuItems.map((m) => m._id));
    const slots: DealSlot[] = (deal.bundleSlots || []).map((sl: any) => ({
      name: sl.name || "",
      options: (sl.options || []).map(idOf).filter((id: string) => available.has(id)),
      upcharges: (sl.upcharges || []).map((u: any) => ({ itemId: idOf(u.itemId), amount: Number(u.amount) || 0 })),
      preselections: sl.preselections || [],
    }));
    if (!slots.length || slots.some((sl) => !sl.options.length)) {
      toast.error("This deal isn't available right now.");
      return;
    }
    const limit = Number(deal.maxUsagePerOrder) || 0;
    const used = (orderData?.appliedDeals || []).filter((d: any) => (d.originalDealId ?? d.dealId) === deal._id).length;
    if (limit > 0 && used >= limit) {
      toast.error(`You can add "${deal.name}" ${limit} time${limit === 1 ? "" : "s"} per order.`);
      return;
    }
    // Items always included in the deal (older deals)
    const fixedCartItems: OrderItem[] = (deal.items || []).map((di: any) => {
      const menuItem = menuItems.find((m) => m._id === idOf(di.itemId));
      const price = Number(menuItem?.price ?? di.itemId?.price ?? 0);
      return {
        itemId: idOf(di.itemId),
        name: menuItem?.name || di.itemId?.name || "Item",
        kitchenName: menuItem?.kitchenName,
        quantity: di.quantity || 1,
        price,
        tax: 0,
        categoryId: String(menuItem?.categoryId ?? ""),
        cartId: Date.now().toString() + Math.random().toString(36).substring(2, 9),
        imageUrl: menuItem?.imageUrl,
        customOptions: [],
        isPartOfDeal: true,
        originalPrice: price,
      } as OrderItem;
    });
    const fixedItemsTotal = fixedCartItems.reduce((sum, it) => sum + it.price * it.quantity, 0);
    handleBundleDealClaim(deal, fixedItemsTotal, fixedCartItems, slots);
  };

  const handleBundleDealClaim = (
    deal: {
      _id: string;
      name: string;
      bundlePrice?: number;
      pricingMode?: "fixed" | "cheapest_free" | "percent_off";
      discountPercent?: number;
      items: { itemId: string | { _id: string } }[];
    },
    fixedItemsTotal: number,
    fixedCartItems: OrderItem[],
    slots: DealSlot[],
  ) => {
    if (slots.length === 0) {
      toast.error("This bundle has no customizable slots configured. Please contact support.");
      return;
    }

    const bundlePrice = deal.bundlePrice ?? 0;
    const claimId = Date.now().toString() + Math.random().toString(36).substring(2, 7);

    // Tag fixed cart items with claimId so cancel only removes this claim's items
    const taggedFixedItems = fixedCartItems.map((item) => ({ ...item, dealId: claimId, dealName: deal.name }));

    setCustomizingItem(null);
    setActiveBundleDeal({
      claimId,
      dealId: deal._id,
      dealName: deal.name,
      bundlePrice,
      fixedItemsTotal,
      slots,
      currentSlotIndex: 0,
      pizzaBasePricesSum: 0,
      pricingMode: deal.pricingMode || "fixed",
      discountPercent: Number(deal.discountPercent) || 0,
      basePrices: [],
      upchargeTotal: 0,
    });

    // Add fixed items to cart and register deal — use claimId so multiple claims are independent
    setOrderData((prev) => ({
      ...prev,
      items: [...(prev?.items || []), ...taggedFixedItems],
      appliedDeals: [
        ...(prev?.appliedDeals || []),
        {
          dealId: claimId,
          originalDealId: deal._id,
          name: deal.name,
          discountAmount: 0,
          dealType: 'bundle' as const,
          bundlePrice,
          bundleFixedItemsTotal: fixedItemsTotal,
        },
      ],
    }));

    // Auto-open customize modal with first item of slot[0] — user can switch via inline pills
    const firstItem = menuItems.find((m) => m._id === slots[0].options[0]);
    if (!firstItem) {
      // Item not loaded — roll back using claimId to avoid stuck state
      setOrderData((prev) => ({
        ...prev,
        items: (prev?.items || []).filter((i) => i.dealId !== claimId),
        appliedDeals: (prev?.appliedDeals || []).filter((d) => d.dealId !== claimId),
      }));
      setActiveBundleDeal(null);
      return;
    }
    // Stage slot[0] preselections — the useEffect will merge them with item defaults
    const slot0Preselections = slots[0].preselections || [];
    if (slot0Preselections.length > 0) {
      const preCustom: Record<string, number> = {};
      slot0Preselections.forEach(({ groupName, choiceLabel }) => {
        preCustom[`${groupName}|||${choiceLabel}`] = 1;
      });
      pendingPreselectionsRef.current = preCustom;
    }
    handlecust(firstItem);
  };

  const showLogo = Boolean(logoUrl);
  const isPageLoading = loading || (showLogo && !isLogoLoaded);

  if (isPageLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <VideoLoader />
      </div>
    );
  }
  const spinnerStyle = `
    input[type="number"]::-webkit-inner-spin-button,
    input[type="number"]::-webkit-outer-spin-button {
      opacity: 1;
      width: 12px;
      height: 12px;
      gap: 10px;
      cursor: pointer;
      transform: scale(2);
      align-self: center;
    }
    
    input[type="number"] {
      padding-right: 40px;
    }
  `;

  return (
    <div className="flex h-screen bg-white">
      {/* Sidebar with compact header + categories */}
      <div
        className="w-[120px] sm:w-[150px] md:w-[200px] lg:w-[230px] relative 
    border-r border-gray-200 flex flex-col bg-white 
    py-3 sm:py-4 md:py-6 px-2 sm:px-2.5 md:px-3 gap-[10px]
    pb-0 no-scrollbar"
        style={{ maxHeight: "100vh" }}
      >
        {/* <div className="flex flex-col items-start gap-2 sm:gap-3 md:gap-4 w-full"> */}
          {/* <div className="flex items-center justify-between w-full gap-2 sm:gap-3 md:gap-4"> */}
            {/* <button
              className=" 
                 p-1.5 sm:p-2 rounded-full bg-transparent items-center flex justify-center 
            w-[28px] h-[28px] sm:w-[32px] sm:h-[32px] md:w-[35px] md:h-[35px] 
            border-[1px] border-[rgba(0,0,0,0.2)] text-black bg-white text-base sm:text-lg 
            font-bold transition "
              onClick={() => navigate("/choice")}
            >
              <img
                src={LeftArrow}
                alt="Back"
                className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4"
              />
            </button> */}
            <div className="flex items-center justify-center min-h-[40px] sm:min-h-[46px] md:min-h-[52px]">
              {showLogo ? (
                <img
                  src={logoUrl}
                  alt="Platform logo"
                  className="w-auto h-[105px]  object-contain"
                />
              ) : (
                <div className="text-left leading-tight">
                  <h2 className="font-bold text-[16px] sm:text-[19px] md:text-[22px] lg:text-[26px] mb-0 leading-tight">
                    Hey
                  </h2>
                  <span className="font-light text-[16px] sm:text-[19px] md:text-[22px] lg:text-[26px] leading-tight">
                    What's up?
                  </span>
                </div>
              )}
            {/* </div> */}
          {/* </div> */}
        </div>
        <div className="flex justify-center w-full">
          <LocationBadge />
        </div>

        <div className="menu-list w-full overflow-y-auto custom-scrollbar no-scrollbar overflow-x-hidden flex-1">
          <ul className="flex flex-col gap-[8px] sm:gap-[10px] p-0">
            {navCategories.map((category) => (
              <li
                className={`flex flex-col rounded-[10px] overflow-x-auto items-start py-[8px] sm:py-[10px] md:py-[12px] 
              px-[8px] sm:px-[12px] md:px-[15px] cursor-pointer transition-transform font-semibold 
                  text-[9px] sm:text-[10px] md:text-[12px] lg:text-[13px] xl:text-[15px]  text-gray-800 
                   hover:scale-103  will-change-transform gap-2 sm:gap-3  ${
                     selectedCategory === category.name
                       ? "bg-main/90 text-white"
                       : "hover:text-black/45"
                   } `}
                key={category.name}
                onClick={() => handleCategorySelect(category.name)}
              >
                <div className="font-semibold 2xl:text-[15px] lg:text-[13px] sm:text-[12px] text-[10px] leading-normal">
                  {category.name}
                </div>
              </li>
            ))}
          </ul>
        </div>
        {/* <DealsModal onByoDealClaim={handleByoDealClaim} onBundleDealClaim={handleBundleDealClaim} /> */}
      </div>

      {/* Menu Section with Category Headers */}
      <div
        className="flex-1 overflow-y-auto no-scrollbar relative"
        ref={menuRef}
      >
        <div className="p-0">
          {error && <div className="menu-error p-3 sm:p-4">{error}</div>}

          {Object.keys(groupedMenuItems).length === 0 ? (
            <p className="text-gray-500">No menu items available.</p>
          ) : (
            <div className="space-y-0">
              {Object.entries(groupedMenuItems).map(
                ([categoryName, items], index) => {
                  const isLastCategory =
                    index === Object.keys(groupedMenuItems).length - 1;

                  return (
                    <div
                      key={categoryName}
                      className="category-section"
                      ref={(el) =>
                        (categorySectionRefs.current[categoryName] = el)
                      }
                      data-category-section={categoryName}
                      style={
                        isLastCategory ? { paddingBottom: "0" } : undefined
                      }
                    >
                      {/* Category Header - Sticky */}
                      <div
                        ref={(el) =>
                          (categoryHeaderRefs.current[categoryName] = el)
                        }
                        data-category-header={categoryName}
                        className={` sticky top-0 z-20
                          flex items-center justify-between
                          bg-catbg
                          px-4 py-4 sm:px-4 md:px-6 lg:px-10
                          mb-3 sm:mb-4 md:mb-5 
                                   [&[data-top='false']_.filter-buttons]:hidden 
                                    ${
                                      stickyStates[categoryName]
                                        ? "bg-catbg max-sm:px-[3px] max-sm:py-2 py-2"
                                        : "bg-catbg py-2 md:py-4"
                                    }`}
                      >
                        <h2
                          className=" font-bold text-gray-900 
                                           text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 2xl:text-4xl"
                        >
                          {categoryName}
                        </h2>
                        {stickyStates[categoryName] &&
                          //                          <div className="filter-buttons p-1 border border-black/5  rounded-xl flex gap-1 sm:gap-2  max-sm:gap-2 md:gap-3 [.sticky:not(:first-child)_&]:hidden ">
                          //                               {/* absolute right-3 top-0 z-50 */}
                          //                               <button
                          //                                 onClick={() =>
                          //                                   setSelectedchoice(
                          //                                     selectedchoice === "veg" ? null : "veg",
                          //                                   )
                          //                                 }
                          //                                 className={`
                          //                                 flex items-center gap-0.5 sm:gap-1 md:gap-2 w-20 sm:w-24 justify-center
                          //                                 px-1.5 sm:px-2.5 md:px-3 md:py-1.5 lg:px-4 lg:py-2 whitespace-nowrap
                          //                                 rounded-xl font-medium transition-all text-[10px] sm:text-[11px] md:text-xs lg:text-sm
                          //                                 ${
                          //                                   selectedchoice === "veg"
                          //                                     ? "bg-toggle"
                          //                                     : " "
                          //                                 }
                          //                                 text-[12px] sm:text-sm md:text-base
                          //                               `}
                          //                             >
                          //                                 <svg
                          //                                   width="13"
                          //                                   height="13"
                          //                                   viewBox="0 0 16 16"
                          //                                   fill="none"
                          //                                 >
                          //                                   <rect
                          //                                     x="1"
                          //                                     y="1"
                          //                                     width="14"
                          //                                     height="14"
                          //                                     stroke="#0F8A65"
                          //                                     strokeWidth="1.5"
                          //                                     fill="none"
                          //                                   />
                          //                                   <circle cx="8" cy="8" r="4" fill="#0F8A65" />
                          //                                 </svg>
                          //                                 <span>Veg</span>
                          //                                 {/* <span className="xs:hidden sm:hidden">V</span> */}
                          //                               </button>
                          //                              <button
                          //   onClick={() =>
                          //     setSelectedchoice(
                          //       selectedchoice === "non-veg"
                          //         ? null
                          //         : "non-veg",
                          //     )
                          //   }
                          //   className={`
                          //     flex items-center gap-0.5 sm:gap-1 md:gap-2 w-20 sm:w-24 justify-center
                          //     px-1.5 sm:px-2.5 md:px-3 py-1.5 lg:px-4 lg:py-2 whitespace-nowrap
                          //     rounded-xl font-medium transition-all text-[10px] sm:text-[11px] md:text-xs lg:text-sm
                          //     ${
                          //       selectedchoice === "non-veg"
                          //         ? "bg-toggle"
                          //         : " "
                          //     }
                          //     text-[12px] sm:text-sm md:text-base
                          //   `}
                          // >
                          //   <svg
                          //     width="13"
                          //     height="13"
                          //     viewBox="0 0 16 16"
                          //     fill="none"
                          //     className="flex-shrink-0"
                          //   >
                          //     <rect
                          //       x="1"
                          //       y="1"
                          //       width="14"
                          //       height="14"
                          //       stroke="#E74C3C"
                          //       strokeWidth="1.5"
                          //       fill="none"
                          //     />
                          //     <circle cx="8" cy="8" r="4" fill="#E74C3C" />
                          //   </svg>
                          //   <span>Non-Veg</span>
                          // </button>
                          //                             </div>
                          " "}
                      </div>

                      {/* Items Grid */}
                      <div
                        className="grid px-3 sm:px-4 md:px-6 lg:px-9 
                  gap-2.5 sm:gap-3 md:gap-4 lg:gap-6 
                  grid-cols-1 md:grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 
                  mb-6 sm:mb-8 md:mb-12 pb-4 sm:pb-6 md:pb-8"
                      >
                        {items.map((item) => {
                          const isCustom =
                            item.customize && item.customize.length > 0;

                          const cartEntry = cart.find(
                            (c) =>
                              c.itemId === item._id &&
                              (!c.customOptions ||
                                c.customOptions.length === 0),
                          );

                          const quantity = cartEntry?.quantity || 0;
                          const cartId = cartEntry?.cartId;

                          const getItemQty = (itemId: string) => {
                            return cart
                              .filter((c) => c.itemId === itemId)
                              .reduce((total, c) => total + c.quantity, 0);
                          };
                          const totalQty = getItemQty(item._id);

                          // Deal cards start the deal; menu items open as usual
                          const dealOfCard = (item as any).isDeal ? (item as any).deal : null;
                          // Deal card colour the admin set (light mode)
                          const cardBg: string = dealOfCard?.bgColor || "";
                          const openCard = () => (dealOfCard ? startDeal(dealOfCard) : handlecust(item));
                          const handlePlusClick = () => {
                            openCard();
                          };

                          return (
                            <div
                              key={item._id}
                              data-item-id={item._id}
                              data-item-category={item.category}
                              className={`relative bg-white rounded-[30px] cursor-pointer flex  gap-2.5 sm:gap-3 md:gap-4 lg:gap-6 
                                   p-[22px] border border-black/5 ${cardBg ? "deal-custom-bg" : ""}`}
                              style={
                                cardBg
                                  ? ({ "--deal-bg": cardBg, "--deal-fg": readableTextColor(cardBg) } as React.CSSProperties)
                                  : undefined
                              }
                              role="button"
                              tabIndex={0}
                              onClick={openCard}
                            >
                              {dealOfCard && (
                                <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]">
                                  <span className="absolute left-[-30px] top-[6px] w-[90px] -rotate-45 bg-orange-600 py-[2px] text-center text-[11px] leading-[14px] font-bold tracking-wider text-white shadow-md">
                                    DEAL
                                  </span>
                                </span>
                              )}
                              <div className="flex-1 flex flex-col justify-center gap-1 sm:gap-1.5 md:gap-2 min-w-0">
                                <h3 className="deal-text text-[13px] sm:text-[14px] md:text-[16px] lg:text-[18px] font-bold text-[rgba(0,0,0,1)] m-0">
                                  {item.name}
                                </h3>
                                <div className="flex items-center m-0 ">
                                  <span className="deal-text text-[13px] sm:text-[14px] md:text-[15px] lg:text-[16px]  font-bold text-[rgba(0,0,0,1)]">
                                    {(item as any).priceLabel ?? `${currencySymbol}${item.price.toFixed(2)}`}
                                  </span>
                                </div>
                                <p
                                  className="deal-text-muted text-[11px] sm:text-[12px] md:text-[13px] lg:text-[12px] 
                            font-normal text-[rgba(0,0,0,1)] m-0 line-clamp-2 hidden sm:block"
                                >
                                  {item.description
                                    ? item.description
                                        .split(" ")
                                        .slice(0, 10)
                                        .join(" ") +
                                      (item.description.split(" ").length > 10
                                        ? "..."
                                        : "")
                                    : ""}
                                </p>

                                {/*  
                              {isCustom && (
                                <div
                                  className="text-center mb-0 mt-2 cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openCard();
                                  }}
                                >
                                  <p className="mb-0 text-sm text-gray-600">Customisable+</p>
                                  border-[1px]  border-[rgba(0,0,0,0.1)]
                                </div>
                              )} */}
                              </div>

                              <div className="flex-shrink-0">
                                <div className="relative ">
                                  <div
                                    className="h-20 w-20 sm:h-24 sm:w-24 md:h-28 md:w-28 lg:h-32 lg:w-32 
                              border-[0px] border-[rgba(0,0,0,0.1)] flex items-center 
                              justify-center rounded-[15px] sm:rounded-[18px] md:rounded-[20px] lg:rounded-[25px]"
                                  >
                                    <img
                                      src={item.imageUrl || "/placeholder.png"}
                                      alt={item.name}
                                      className="h-full w-full rounded-[10px] sm:rounded-[12px] md:rounded-[15px] 
                                  object-cover"
                                      onError={(e) => {
                                        (
                                          e.currentTarget as HTMLImageElement
                                        ).src = "/placeholder.png";
                                      }}
                                    />
                                  </div>
                                  {/* Veg / Non-Veg Logo */}
                                  {SHOW_FOOD_TYPE_ICON && item.foodType !== "both" && (
                                    <img
                                      src={
                                        item.foodType === "veg"
                                          ? vegIcon
                                          : nonvegIcon
                                      }
                                      alt={item.foodType}
                                      className="
                                    absolute -top-[10px] -right-[5px] sm:-top-[12px] sm:-right-[6px] 
                                  md:-top-[13px] md:-right-[10px] 
                                  w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4"
                                    />
                                  )}
                                  <div
                                    className="flex justify-center items-center absolute 
                              -bottom-2 sm:-bottom-2.5 md:-bottom-3 left-0 right-0 mx-auto"
                                  >
                                    {totalQty === 0 ? (
                                      <button
                                        className="bg-main px-2 py-0.5 sm:px-2.5 sm:py-1 md:px-3 md:py-1 
                                    rounded-[5px] sm:rounded-[6px] md:rounded-[8px] w-16
                                    text-[11px] sm:text-[12px] md:text-[13px] lg:text-[15px] 
                                    font-semibold text-white shadow-md"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openCard();
                                        }}
                                      >
                                        Add +
                                      </button>
                                    ) : (
                                      <div
                                        className="flex items-center justify-between gap-1.5 bg-white  rounded-[5px] sm:rounded-[6px] md:rounded-[8px] w-16 border border-black/30 shadow-md"
                                        onClick={(e) => e.stopPropagation()}
                                        role="group"
                                        aria-label={`Quantity controls for ${item.name}`}
                                      >
                                        <button
                                          className="w-5 h-5 rounded-[10px] border-none bg-white font-bold cursor-pointer flex items-center justify-center"
                                          onClick={() => {
                                            // Find the most recently added item (customized or not)
                                            const allItemVariants = cart.filter(
                                              (c) => c.itemId === item._id,
                                            );

                                            if (allItemVariants.length === 0)
                                              return;

                                            // Get the last added variant
                                            const lastVariant =
                                              allItemVariants[
                                                allItemVariants.length - 1
                                              ];

                                            if (lastVariant.quantity <= 1) {
                                              removeFromCart(
                                                lastVariant.cartId,
                                              );
                                            } else {
                                              updateQuantity(
                                                lastVariant.cartId,
                                                lastVariant.quantity - 1,
                                              );
                                            }
                                          }}
                                        >
                                          −
                                        </button>

                                        <span className="qty-count">
                                          {String(totalQty)}
                                        </span>

                                        <button
                                          className="w-5 h-5 rounded-[10px] border-none text-black font-bold cursor-pointer flex items-center justify-center"
                                          onClick={handlePlusClick}
                                        >
                                          +
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className=" flex justify-center items-center mx-auto mt-3">
                                  {/* {isCustom && (
                                <div
                                  className="text-center mt-1.5 sm:mt-2 md:mt-3 cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openCard();
                                  }}
                                >
                                  <p className="mb-0 text-[9px] sm:text-[9px] md:text-[10px] lg:text-[11px]
                                text-gray-600 hover:text-gray-800 transition-colors">Customisable+</p>
                                </div>
                              )} */}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          )}
        </div>
      </div>

      {/* Cart Toggle + Overlay (mobile) */}
      {isDrawer && !isCartOpen && (
        <button
          className="flex items-center gap-2.5 fixed
          right-[max(16px,env(safe-area-inset-right,16px))]
          bottom-[max(16px,env(safe-area-inset-bottom,16px))]
          px-[14px] py-[12px]
          rounded-full
          bg-main
          text-white font-semibold
          border-0
           z-[1000]
          cursor-pointer lg:hidden"
          aria-controls="cart"
          aria-expanded={isCartOpen}
          aria-label="Open cart"
          onClick={toggleCart}
        >
          <ShoppingCart size={22} className="shrink-0" />
          <span className="leading-none">Cart ({totalItems})</span>
        </button>
      )}

      {/* Overlay - separate from button, shows when cart is open */}
      {isDrawer && (
        <div
          role="presentation"
          aria-hidden={!isCartOpen}
          className={`fixed inset-0 bg-black/50 z-[98] lg:hidden ${
            isCartOpen ? "block" : "hidden"
          }`}
          onClick={closeCart}
        />
      )}

      {/* Cart Drawer / Panel */}
      <div
        id="cart"
        role={isDrawer ? "dialog" : undefined}
        aria-modal={isDrawer || undefined}
        aria-label="Shopping cart"
        className={`
           w-[350px] bg-white border-l border-gray-200 flex flex-col justify-start 
  h-screen 
  fixed top-0 right-0 transition-transform duration-300 z-[99] 
  ${isDrawer && isCartOpen ? "translate-x-0" : "translate-x-full"}
  min-[1024px]:w-[300px] min-[1024px]:static min-[1024px]:translate-x-0
  
`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            {isDrawer && (
              <button
                aria-label="Back to menu"
                onClick={closeCart}
                className="bg-transparent border-0 p-2 rounded-lg font-bold text-black flex items-center justify-center"
              >
                <img
                  src={LeftArrow}
                  alt="Back"
                  className="w-5 h-5 object-contain"
                />
              </button>
            )}
            <span className="font-bold text-lg text-main">Your Cart</span>
            <button
              className="text-sm text-black font-medium hover:underline disabled:text-gray-400"
              disabled={cart.length === 0}
              onClick={() => setOrderData((prev) => ({ ...prev, items: [] }))}
            >
              Clear All
            </button>
          </div>
          {/* Enhanced Cart Display with Deal Grouping */}
          <div className="cart-body px-1 py-1 no-scrollbar">
            {cart.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500 font-medium">
                Your cart is empty
              </div>
            ) : (
              <div className="space-y-1">
                {/* Group items by deal */}
                {(() => {
                  // Get unique dealIds
                  const dealIds = Array.from(
                    new Set(
                      cart
                        .filter((item) => item.isPartOfDeal)
                        .map((item) => item.dealId),
                    ),
                  ).filter((id): id is string => id !== undefined);
                  const regularItems = cart.filter(
                    (item) => !item.isPartOfDeal,
                  );

                  return (
                    <>
                      {/* Regular Items */}
                      {regularItems.map((cartItem) => (
                        <div
                          className="flex items-center justify-between bg-white p-2 rounded-lg border-[1px] border-[rgba(238,238,238,1)] w-full"
                          key={cartItem.cartId}
                          onClick={(e) => {
                            if (
                              cartItem.customOptions &&
                              cartItem.customOptions.length > 0
                            ) {
                              e.stopPropagation();
                              const menuItem = menuItems.find(
                                (m) => m._id === cartItem.itemId,
                              );
                              setCustomizingItem(cartItem);
                              setSelectedCustomizeItem(menuItem || null);
                              const populated = buildSelectionsFromOptions(
                                menuItem,
                                cartItem.customOptions || [],
                              );
                              setCustomSelections(populated);
                              setShowcustModal(true);
                            }
                          }}
                        >
                          {/* Existing cart item UI */}
                          <div className="flex items-center gap-3 w-full">
                            <img
                              src={cartItem.imageUrl || "/placeholder.png"}
                              alt={cartItem.name}
                              className="w-14 h-14 object-contain rounded-md border border-gray-200"
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).src =
                                  "/placeholder.png";
                              }}
                            />
                            <div className="flex flex-col w-full">
                              <div className="flex text-sm font-bold justify-between w-full">
                                <span className="text-gray-900 text-sm break-words whitespace-normal max-w-[160px] line-clamp-3">
                                  {cartItem.name}
                                </span>
                              </div>

                              {cartItem.customOptions &&
                                cartItem.customOptions.length > 0 && (
                                  <div className="mt-1 text-xs text-gray-600">
                                    <div className="h-auto max-h-[45px] w-9/12 no-scrollbar overflow-y-auto">
                                      <span className="font-medium">
                                        {getDisplayAddedOptions(cartItem).length > 0 && (
                                          <>
                                            Added:{" "}
                                            {getDisplayAddedOptions(cartItem)
                                              .map((opt) => `${opt.label}${opt.qty > 1 ? ` ×${opt.qty}` : ""}`)
                                              .join(", ")}
                                          </>
                                        )}
                                        {getDisplayRemovedOptions(cartItem).length > 0 && (
                                          <>
                                            {getDisplayAddedOptions(cartItem).length > 0 && " | "}
                                            Removed:{" "}
                                            {getDisplayRemovedOptions(cartItem)
                                              .map((opt) => `${opt.label}${opt.qty > 1 ? ` ×${opt.qty}` : ""}`)
                                              .join(", ")}
                                          </>
                                        )}

                                      </span>
                                    </div>
                                  </div>
                                )}

                              <div className="inline-flex items-center justify-between mt-2 ">
                                <span className="text-gray-900 text-sm font-bold">
                                  {currencySymbol}{Number(cartItem.price).toFixed(2)}
                                </span>
                                <div className="flex items-center border border-black/10 rounded-xl">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateQuantity(
                                        cartItem.cartId,
                                        cartItem.quantity - 1,
                                      );
                                    }}
                                    className="w-8 h-8 rounded-md font-bold flex items-center justify-center text-black "
                                  >
                                    -
                                  </button>

                                  <div className="flex items-center justify-center w-6 h-5 gap-0">
                                    <span className="font-medium text-gray-800">
                                      {String(cartItem.quantity)}
                                    </span>
                                  </div>

                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateQuantity(
                                        cartItem.cartId,
                                        cartItem.quantity + 1,
                                      );
                                    }}
                                    className="w-8 h-8 rounded-md  font-bold flex items-center justify-center text-black"
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Deal Items Grouped */}
                      {dealIds.map((dealId) => {
                        const dealItems = cart.filter(
                          (item) => item.dealId === dealId,
                        );
                        const deal = appliedDeals.find(
                          (d) => d.dealId === dealId,
                        );

                        if (!dealItems.length) return null;

                        return (
                          <div
                            key={dealId}
                            className="bg-gradient-to-r from-yellow-20 to-yellow-20 p-3 rounded-lg border-2 border-black/50"
                          >
                            {/* Deal Header */}
                            <div className="flex items-center justify-between mb-2 pb-2 border-b-2 border-black/50">
                              <div className="flex items-center gap-2">
                                {/* <span className="text-lg">🎉</span> */}
                                <p className="m-0 font-bold text-sm text-gray-900">
                                  {deal?.name || "Deal"}
                                </p>
                              </div>
                              <button
                                onClick={() => {
                                  if (!dealId) return;

                                  // Show confirmation modal for deal removal
                                  setConfirmModal({
                                    open: true,
                                    dealId: dealId,
                                    itemName: deal?.name || "Deal",
                                    isDeal: true,
                                  });
                                }}
                                className="font-bold text-sm"
                              >
                                x
                              </button>
                            </div>

                            {/* Deal Items */}
                            <div className="space-y-2">
                              {dealItems.map((cartItem) => {
                                const isFreeItem =
                                  deal?.dealType === "buy_x_get_y" &&
                                  cartItem.price === 0;

                                const dealMenuItemForEdit = cartItem.customOptions && cartItem.customOptions.length > 0
                                  ? menuItems.find((m) => m._id === cartItem.itemId)
                                  : undefined;

                                return (
                                  <div
                                    key={cartItem.cartId}
                                    className="flex items-center gap-2  p-2 rounded-md"
                                  >
                                    <img
                                      src={
                                        cartItem.imageUrl || "/placeholder.png"
                                      }
                                      alt={cartItem.name}
                                      className="w-10 h-10 object-contain rounded-md border border-gray-200"
                                    />
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-semibold text-gray-900">
                                            {cartItem.name}
                                          </span>
                                          {isFreeItem && (
                                            <span className="text-xs text-black px-2 py-0.5 rounded-full font-bold">
                                              FREE
                                            </span>
                                          )}
                                        </div>
                                        {dealMenuItemForEdit && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const dealEntry = appliedDeals.find((d) => d.dealId === dealId);
                                              setCustomizingItem(cartItem);
                                              setSelectedCustomizeItem(dealMenuItemForEdit);
                                              const populated = buildSelectionsFromOptions(
                                                dealMenuItemForEdit,
                                                cartItem.customOptions || [],
                                              );
                                              setCustomSelections(populated);
                                              setEditingDealMeta({
                                                dealType: dealEntry?.dealType || 'fixed_price',
                                                byoBasePrice: dealEntry?.byoBasePrice,
                                                byoIncludedToppings: dealEntry?.byoIncludedToppings,
                                                bundlePrice: dealEntry?.bundlePrice,
                                                bundleFixedItemsTotal: dealEntry?.bundleFixedItemsTotal,
                                              });
                                              setShowcustModal(true);
                                            }}
                                            className="p-1 hover:bg-gray-100 rounded-full flex-shrink-0"
                                            title="Edit customizations"
                                          >
                                            <Pencil size={13} className="text-gray-500" />
                                          </button>
                                        )}
                                      </div>
                                      {cartItem.customOptions && cartItem.customOptions.length > 0 && (
                                        <div className="mt-0.5 text-xs text-gray-500">
                                          <div className="max-h-[36px] overflow-y-auto no-scrollbar">
                                            {getDisplayAddedOptions(cartItem).length > 0 && (
                                              <span>
                                                Added: {getDisplayAddedOptions(cartItem).map((o) => `${o.label}${o.qty > 1 ? ` ×${o.qty}` : ""}`).join(", ")}
                                              </span>
                                            )}
                                            {getDisplayRemovedOptions(cartItem).length > 0 && (
                                              <span>
                                                {getDisplayAddedOptions(cartItem).length > 0 && " | "}
                                                Removed: {getDisplayRemovedOptions(cartItem).map((o) => o.label).join(", ")}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      <div className="flex items-center gap-2 text-xs text-black">
                                        <span>Qty: {cartItem.quantity}</span>
                                        {!isFreeItem && (
                                          <>
                                            {cartItem.originalPrice &&
                                              cartItem.originalPrice !==
                                                cartItem.price && (
                                                <span className="line-through text-black">
                                                  $
                                                  {(
                                                    cartItem.originalPrice *
                                                    cartItem.quantity
                                                  ).toFixed(2)}
                                                </span>
                                              )}
                                            <span className="font-semibold text-black">
                                              $
                                              {(
                                                cartItem.price *
                                                cartItem.quantity
                                              ).toFixed(2)}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Recommendations Section - NEW */}
          {cart.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 bg-white">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-bold text-base text-gray-900">
                  Add a Drink?
                </h3>
              </div>

              <div className="flex gap-3 overflow-x-auto scrollbar-thin">
                {menuItems
                  .filter(
                    (item) =>
                      item.category?.toLowerCase() === "drinks" ||
                      item.category?.toLowerCase() === "beverages",
                  )
                  .slice(0, 5)
                  .map((drink) => {
                    const isInCart = cart.some(
                      (cartItem) => cartItem.itemId === drink._id,
                    );

                    return (
                      <div
                        key={drink._id}
                        className="flex-shrink-0 w-[100px] bg-white rounded-lg border border-gray-200 p-2 shadow-sm flex flex-col"
                      >
                        <img
                          src={drink.imageUrl || "/placeholder.png"}
                          alt={drink.name}
                          className="w-full h-12 object-contain rounded-md mb-2"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).src =
                              "/placeholder.png";
                          }}
                        />
                        <p className="text-xs font-semibold text-gray-900 line-clamp-2 mb-2 text-center min-h-[32px]">
                          {drink.name}
                        </p>
                        <div className="flex items-center justify-between mt-auto">
                          <span className="text-xs font-bold text-gray-900">
                            {currencySymbol}{Number(drink.price).toFixed(2)}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isInCart) {
                                const newCartItem = {
                                  cartId: `${drink._id}-${Date.now()}`,
                                  itemId: drink._id,
                                  name: drink.name,
                                  kitchenName: drink.kitchenName,
                                  price: drink.price,
                                  quantity: 1,
                                  imageUrl: drink.imageUrl,
                                  customOptions: [],
                                  isPartOfDeal: false,
                                };
                                setOrderData((prev: any) => {
                                  if (!prev) return prev;
                                  return {
                                    ...prev,
                                    items: [...(prev.items || []), newCartItem],
                                  };
                                });
                              }
                            }}
                            disabled={isInCart}
                            className={`text-xs font-bold px-2 py-1 rounded-md transition-all border border-black/30 ${
                              isInCart
                                ? "bg-main/20 text-black cursor-not-allowed"
                                : "bg-white text-black"
                            }`}
                          >
                            {isInCart ? "✓" : "+"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Enhanced Cart Footer with Deal Breakdown */}

          {/* Summary + Checkout (current structure) */}
          <div className="cart-footer p-4 border-t border-black/5 flex flex-col gap-1">
            <div className="flex text-[rgba(0,0,0,1)] justify-between text-sm font-semibold">
              <span>Subtotal:</span>
              <span>{currencySymbol}{subtotal.toFixed(2)}</span>
            </div>

            {totalDealDiscount > 0.001 && (
              <div className="flex justify-between text-black text-sm font-medium">
                <span>Deal Discount:</span>
                <span>-{currencySymbol}{totalDealDiscount.toFixed(2)}</span>
              </div>
            )}
            {totalDealDiscount < -0.001 && (
              <div className="flex justify-between text-black text-sm font-medium">
                <span>Bundle Pricing:</span>
                <span>+${Math.abs(totalDealDiscount).toFixed(2)}</span>
              </div>
            )}

            {loyaltyDiscountAmount > 0 && (
              <div className="flex justify-between text-black text-sm font-semibold">
                <span>Loyalty Discount ({discount}%):</span>
                <span>-{currencySymbol}{loyaltyDiscountAmount.toFixed(2)}</span>
              </div>
            )}

            {totalGst + totalPst > 0 ? (
              // Show both lines (PST can legitimately be $0.00, e.g. prepared food in BC)
              <>
                <div className="flex justify-between text-black text-sm font-semibold">
                  <span>GST:</span>
                  <span>{currencySymbol}{totalGst.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-black text-sm font-semibold">
                  <span>PST:</span>
                  <span>{currencySymbol}{totalPst.toFixed(2)}</span>
                </div>
              </>
            ) : (
              <div className="flex justify-between text-black text-sm font-semibold">
                <span>Taxes:</span>
                <span>{currencySymbol}{totalTax.toFixed(2)}</span>
              </div>
            )}

            <div className="flex justify-between text-black text-[15px] font-bold border-t-2 border-gray-300 pt-2">
              <span>Total:</span>
              <span>{currencySymbol}{grandTotal.toFixed(2)}</span>
            </div>

            <div className="flex flex-col">
              <div className="flex flex-col text-xs gap-1.5">
                <label htmlFor="phoneNumber">Phone Number</label>
                <input
                  className=" py-1 px-3 rounded-lg text-xs font-normal bg-white text-black outline-none border-[1px] border-[rgba(238,238,238,1)] font-montserrat"
                  id="phoneNumber"
                  type="tel"
                  inputMode="numeric"
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                  maxLength={12}
                  placeholder="Enter phone number"
                />
              </div>

              <div className="relative">
                <label className="mb-[4px] text-xs " htmlFor="tableToken">
                  Table Token
                </label>
                <style>{spinnerStyle}</style>
                <input
                  type="number"
                  id="tableToken"
                  min="1"
                  max="20"
                  value={tableToken === "" ? "" : tableToken}
                  onChange={handleChange}
                  placeholder="0"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onKeyDown={(e) => {
                    if (
                      !/[0-9]/.test(e.key) &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete" &&
                      e.key !== "ArrowLeft" &&
                      e.key !== "ArrowRight" &&
                      e.key !== "Tab"
                    ) {
                      e.preventDefault();
                    }
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => {
                    e.currentTarget.blur();
                  }}
                  className="w-full py-1 px-3  text-xs rounded-lg bg-white font-xs text-black outline-none border-[1px] border-[rgba(238,238,238,1)] font-montserrat transition-colors"
                />
              </div>
            </div>

            <button
              onClick={handleCheckout}
              className="bg-black border-0 rounded-[10px] font-semibold cursor-pointer mt-2  text-[15px] w-full px-3 py-[10px] text-white"
              disabled={loading}
            >
              {loading ? "Processing..." : "Checkout"}
            </button>
          </div>
        </div>
      </div>

      {/* BYO Pizza Picker — shown before customize modal for multi-item deals */}
      {byoPicker && activeBYODeal && (
        <div className="fixed inset-0 z-[9998] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-black text-lg text-gray-900">{activeBYODeal.dealName}</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Pizza {byoPicker.pizzaIndex + 1} of {activeBYODeal.minPizzas} — Choose your pizza
                  </p>
                </div>
                <button
                  onClick={() => {
                    cancelBYODeal(activeBYODeal.claimId);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition"
                >
                  <span className="text-xl font-bold">✕</span>
                </button>
              </div>
              {activeBYODeal.includedToppings > 0 && (
                <div className="mt-2 px-3 py-1.5 bg-blue-50 border border-blue-100 rounded-lg">
                  <p className="text-xs text-blue-700 font-medium">
                    First {activeBYODeal.includedToppings} toppings free · Extra toppings at menu price
                  </p>
                </div>
              )}
            </div>

            {/* Pizza Grid */}
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                {activeBYODeal.itemIds.map((itemId) => {
                  const item = menuItems.find(m => m._id === itemId);
                  if (!item) return null;
                  return (
                    <button
                      key={itemId}
                      onClick={() => {
                        setByoPicker(null);
                        handlecust(item);
                      }}
                      className="flex flex-col items-center bg-gray-50 hover:bg-orange-50 border-2 border-gray-200 hover:border-main rounded-xl p-3 transition-all duration-200 text-left group"
                    >
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          className="w-full h-24 object-cover rounded-lg mb-2"
                        />
                      ) : (
                        <div className="w-full h-24 bg-gray-200 rounded-lg mb-2 flex items-center justify-center text-gray-400 text-3xl">
                          🍕
                        </div>
                      )}
                      <span className="font-semibold text-sm text-gray-900 text-center leading-tight group-hover:text-main">
                        {item.name}
                      </span>
                      <span className="text-xs text-gray-500 mt-1">
                        ${activeBYODeal.basePrice.toFixed(2)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customisable Modal */}
      {selectedCustomizeItem && (
        <Modal
          show={showcustModal}
          onHide={() => {
            setShowcustModal(false);
            setResetSelections((c) => c + 1);
            setCustomSelections({});
            setHasAttemptedSubmit(false);
            if (activeBYODeal) cancelBYODeal(activeBYODeal.claimId);
            if (activeBundleDeal) cancelBundleDeal(activeBundleDeal.claimId);
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.delete("item");
            window.history.pushState({}, "", currentUrl.toString());
          }}
          dialogClassName="flex items-center justify-center h-[100vh] my-0"
          contentClassName="rounded-[20px] bg-white 
                 pb-0 shadow-[0_6px_18px_rgba(0,0,0,0.12)] 
                max-w-[500px] max-h-[70vh] overflow-y-auto no-scrollbar relative"
        >
          <Modal.Body className="p-0">
            {/* Sticky Header with Image + Close Button */}
            <div className="sticky top-0 z-30 bg-white">
              <button
                onClick={() => {
                  setShowcustModal(false);
                  setResetSelections((c) => c + 1);
                  setCustomSelections({});
                  setHasAttemptedSubmit(false);
                  if (activeBYODeal) cancelBYODeal(activeBYODeal.claimId);
            if (activeBundleDeal) cancelBundleDeal(activeBundleDeal.claimId);
                  const currentUrl = new URL(window.location.href);
                  currentUrl.searchParams.delete("item");
                  window.history.pushState({}, "", currentUrl.toString());
                }}
                className="absolute top-2 right-2 bg-main text-white font-bold rounded-full w-8 h-8 flex items-center justify-center shadow-md"
              >
                ✕
              </button>

              {/* BYO deal progress banner */}
              {activeBYODeal && (
                <div className="mx-2 mt-8 mb-1 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs font-bold text-blue-800 mb-0">
                    {activeBYODeal.dealName} — Pizza {activeBYODeal.pizzasDone + 1} of {activeBYODeal.minPizzas}
                  </p>
                  {activeBYODeal.includedToppings > 0 && (
                    <p className="text-xs text-blue-600 mb-0">
                      First {activeBYODeal.includedToppings} toppings free • Extra toppings priced per item
                    </p>
                  )}
                </div>
              )}

              {/* placeholder to clear the absolute close button */}
              {(activeBYODeal || activeBundleDeal) && <div className="h-10" />}
            </div>
            <div className="relative">
              <img
                src={selectedCustomizeItem.imageUrl || "/placeholder.png"}
                alt={selectedCustomizeItem.name}
                className="w-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src =
                    "/placeholder.png";
                }}
              />
            </div>
            {/* Bundle deal strip — shown below item image */}
            {activeBundleDeal && (
              <div className="bg-main/5 border-y border-main/20 px-3 py-2">
                <div className="flex items-center justify-between mb-0">
                  <p className="text-xs font-bold text-main mb-0">🎁 {activeBundleDeal.dealName}</p>
                  <p className="text-xs font-semibold text-main mb-0">
                    {(() => {
                      const priceText =
                        activeBundleDeal.pricingMode === "cheapest_free"
                          ? "Cheapest free"
                          : activeBundleDeal.pricingMode === "percent_off"
                            ? `${activeBundleDeal.discountPercent}% off`
                            : `${cs()}${activeBundleDeal.bundlePrice.toFixed(2)}`;
                      const stepName = activeBundleDeal.slots[activeBundleDeal.currentSlotIndex]?.name;
                      return activeBundleDeal.slots.length > 1
                        ? `Step ${activeBundleDeal.currentSlotIndex + 1}/${activeBundleDeal.slots.length}${stepName ? ` · ${stepName}` : ""} · ${priceText}`
                        : `${stepName ? `${stepName} · ` : ""}${priceText}`;
                    })()}
                  </p>
                </div>
                {activeBundleDeal.slots[activeBundleDeal.currentSlotIndex]?.options.length > 1 && (
                  <div className="flex gap-1.5 overflow-x-auto no-scrollbar mt-1.5">
                    {activeBundleDeal.slots[activeBundleDeal.currentSlotIndex].options.map((itemId) => {
                      const item = menuItems.find((m) => m._id === itemId);
                      if (!item) return null;
                      const isSelected = selectedCustomizeItem?._id === itemId;
                      return (
                        <button
                          key={itemId}
                          onClick={() => {
                            const currentPreselections = activeBundleDeal?.slots[activeBundleDeal.currentSlotIndex]?.preselections || [];
                            if (currentPreselections.length > 0) {
                              const preCustom: Record<string, number> = {};
                              currentPreselections.forEach(({ groupName, choiceLabel }) => {
                                preCustom[`${groupName}|||${choiceLabel}`] = 1;
                              });
                              pendingPreselectionsRef.current = preCustom;
                            }
                            setSelectedCustomizeItem(item);
                            setResetSelections((c) => c + 1);
                          }}
                          className={`flex-shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-all duration-150 ${
                            isSelected
                              ? 'bg-main text-white border-main'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-main hover:text-main'
                          }`}
                        >
                          {item.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <div className="p-3">
              <h3 className="font-bold text-lg text-black mb-[10px] text-left mt-2">
                {selectedCustomizeItem.name}
              </h3>
              <p
                className="text-xs text-black font-outfit h-auto overflow-hidden 
                      text-ellipsis [display:-webkit-box] [-webkit-box-orient:vertical] text-left mt-2"
              >
                {selectedCustomizeItem.description}
              </p>
              {(() => {
            const isHalfHalfPizza = selectedCustomizeItem.category
                  ?.toLowerCase()
                  .includes("pizza")
                  || selectedCustomizeItem.customize.some((grp) => grp.portionOptions);

                return selectedCustomizeItem?.customize.map((group, gIndex) => {
                // Per-group addon limit values
                const freeToppingCount = activeBYODeal
                  ? activeBYODeal.includedToppings
                  : (group.noOfFreeToppings ?? 0);
                const maxToppings = group.maxToppingsAllowed;
                const minAddons = group.minAddons ?? 0;

                const totalSelectedInGroup = group.choices.reduce((sum, c) => {
                  if (c.default) return sum;
                  const k = `${group.name}|||${c.label}`;
                  return sum + Math.max(0, Number(customSelections[k] || 0));
                }, 0);
                const hasReachedMaxToppings =
                  !!maxToppings &&
                  maxToppings > 0 &&
                  totalSelectedInGroup >= maxToppings;

                // Pre-compute free quota available before each choice in this group consumes it
                const freeBeforeChoice = new Map<string, number>();
                if (freeToppingCount > 0) {
                  let freeRem = freeToppingCount;
                  for (const c of group.choices) {
                    if (c.default) continue;
                    const k = `${group.name}|||${c.label}`;
                    freeBeforeChoice.set(k, freeRem);
                    const qty = Number(customSelections[k] || 0);
                    if (qty > 0 && freeRem > 0) freeRem -= Math.min(qty, freeRem);
                  }
                }
                  const hasSelection = (group.choices || []).some((choice) => {
                    const key = `${group.name}|||${choice.label}`;
                    return Number(customSelections[key] || 0) > 0;
                  });
                  const isCollapsed = collapsedGroups[group.name] ?? false;
                  return (
                  <div
                    key={gIndex}
                    ref={(el) => {
                      customizationGroupRefs.current[`group-${gIndex}`] = el;
                    }}
                    className={`mb-3 border-b-2 rounded-lg p-2 transition-all duration-200 ${
                      hasAttemptedSubmit && (
                        (group.required && !hasSelection) ||
                        (minAddons > 0 && totalSelectedInGroup < minAddons)
                      )
                        ? "border-2 border-red-500"
                        : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="w-full flex items-center justify-between gap-2 mb-2 cursor-pointer select-none group"
                      onClick={() =>
                        setCollapsedGroups((prev) => ({
                          ...prev,
                          [group.name]: !isCollapsed,
                        }))
                      }
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-base font-semibold">{group.name}</span>
                        {group.required && (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            hasSelection ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
                          }`}>
                            {hasSelection ? "Selected" : "Required"}
                          </span>
                        )}
                        {!group.required && freeToppingCount > 0 && (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                            {Math.min(totalSelectedInGroup, freeToppingCount)}/
                            {freeToppingCount} free
                          </span>
                        )}
                        {!group.required &&
                          maxToppings !== undefined &&
                          maxToppings > 0 && (
                            <span
                              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                totalSelectedInGroup >= maxToppings
                                  ? "bg-red-100 text-red-600"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {totalSelectedInGroup}/{maxToppings} max
                            </span>
                          )}
                        {/* {!group.required && minAddons > 0 && (
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              totalSelectedInGroup >= minAddons
                                ? "bg-blue-100 text-blue-700"
                                : "bg-orange-100 text-orange-600"
                            }`}
                          >
                            {totalSelectedInGroup}/{minAddons} min
                          </span>
                        )} */}
                      </div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={`shrink-0 text-gray-400 group-hover:text-gray-600 transition-transform duration-300 ${
                          isCollapsed ? "-rotate-90" : "rotate-0"
                        }`}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                    <div className={`grid transition-all duration-300 ease-in-out ${isCollapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]"}`}>
                    <div className="overflow-hidden">
                    <div className="grid grid-rows">
                      {(() => {
                        // Single selection radio (required single choice)
                        if (
                          group.multiple === false &&
                          group.required === true
                        ) {
                          // Find the bundle-preselected choice for this group (if any)
                          const preselectedChoiceInGroup = group.choices.find((c) =>
                            lockedKeys.has(`${group.name}|||${c.label}`)
                          );
                          const preselectedPrice = preselectedChoiceInGroup?.price ?? 0;

                          return [...group.choices].sort((a, b) => (b.default ? 1 : 0) - (a.default ? 1 : 0)).map((choice, idx) => {
                            const key = `${group.name}|||${choice.label}`;
                            const isPreselected = lockedKeys.has(key);
                            // Disable choices cheaper than the bundle-included option (prevent price reduction)
                            const isDisabledByPrice = !!preselectedChoiceInGroup && (choice.price ?? 0) < preselectedPrice;
                            const quantity =
                              (customSelections[key] as number) || 0;
                            const selectedSection =
                              (customSelections[`${key}_section`] as string) ||
                              "";

                       const showHalfHalfOptions = shouldShowPortionSelector(isHalfHalfPizza, group);

                            return (
                              <div key={idx} className={isDisabledByPrice ? "opacity-40" : ""}>
                                <div
                                  className="flex items-center p-1 gap-1 mb-1"
                                  style={{ cursor: isDisabledByPrice ? "not-allowed" : "pointer" }}
                                  onClick={() => {
                                    if (isDisabledByPrice) return;
                                    group.choices.forEach((c) => {
                                      const otherKey = `${group.name}|||${c.label}`;
                                      handleChoiceChange(otherKey, 0);
                                    });
                                    handleChoiceChange(key, 1);
                                  }}
                                >
                                  <input
                                    type="radio"
                                    name={group.name}
                                    checked={quantity === 1}
                                    onChange={() => {}}
                                    className="w-4 h-4 accent-main appearance-none rounded-full border border-black checked:border-main checked:bg-main focus:outline-none focus:ring-0 focus:ring-offset-0"
                                    style={{ pointerEvents: "none" }}
                                  />
                                  <div className="flex items-center space-x-3 w-full">
                                    {choice.image && (
                                      <img
                                        src={choice.image}
                                        alt={choice.label}
                                        className="w-14 h-14 object-cover rounded-md"
                                      />
                                    )}
                                    <div className="flex flex-row w-full justify-between">
                                      <p
                                        className="text-sm font-medium m-0 cursor-pointer"
                                      >
                                        {choice.label}
                                        {isPreselected && quantity === 1 && (
                                          <span className="text-xs text-gray-500 ml-1">(Included)</span>
                                        )}
                                        {selectedSection && (
                                          <span className="text-xs text-black ml-1">
                                            -{" "}
                                            {selectedSection
                                              .charAt(0)
                                              .toUpperCase() +
                                              selectedSection.slice(1)}
                                          </span>
                                        )}
                                      </p>
                                      <p className="text-sm font-medium m-0">
                                        {choice.price > 0 && !(isPreselected && quantity === 1) &&
                                          `${currencySymbol}${choice.price.toFixed(2)}`}
                                        {isPreselected && quantity === 1 && (
                                          <span className="text-xs text-green-600 font-medium">Included</span>
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        }

                        // Multiple Addons - qty stepper per choice
                        if (group.multipleAddons) {
                          return group.choices.map((choice, idx) => {
                            const key = `${group.name}|||${choice.label}`;
                            const qty = (customSelections[key] as number) || 0;
                            const isMaxed = hasReachedMaxToppings;
                            return (
                              <div key={idx} className="flex items-center justify-between p-1.5 gap-2">
                                <div className="flex items-center gap-3">
                                  {choice.image && (
                                    <img
                                      src={choice.image}
                                      alt={choice.label}
                                      className="w-12 h-12 object-cover rounded-md"
                                    />
                                  )}
                                  <div>
                                    <p className="text-sm font-medium m-0">{choice.label}</p>
                                    {choice.price > 0 && (
                                      <p className="text-xs text-gray-500 m-0">+{currencySymbol}{choice.price.toFixed(2)}</p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                   {qty > 0 && (
                                            <>
                                              <button
                                                type="button"
                                                onClick={() => handleChoiceChange(key, qty - 1)}
                                                className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-lg font-semibold hover:bg-gray-100 transition"
                                              >
                                                −
                                              </button>
                                              <span className="w-5 text-center text-sm font-semibold">{qty}</span>
                                            </>
                                          )}
                                  <button
                                    type="button"
                                    onClick={() => !isMaxed && handleChoiceChange(key, qty + 1)}
                                    disabled={isMaxed}
                                    className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-lg font-semibold disabled:opacity-30 hover:bg-gray-100 transition"
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            );
                          });
                        }

                        // Multiple selection - checkboxes
                        if (group.multiple) {
                          return [...group.choices].sort((a, b) => (b.default ? 1 : 0) - (a.default ? 1 : 0)).map((choice, idx) => {
                            const key = `${group.name}|||${choice.label}`;
                            const isLocked = lockedKeys.has(key);
                            const isChecked =
                              ((customSelections[key] as number) || 0) > 0;
                            const isChoiceMaxedOut =
                              !isLocked &&
                              hasReachedMaxToppings &&
                              !isChecked;
                            if (isChoiceMaxedOut) return null;
                            const selectedSection =
                              (customSelections[`${key}_section`] as string) ||
                              "";

                            // Use group's showPortionSelector setting, fallback to old logic for backward compatibility
                       const showHalfHalfOptions = shouldShowPortionSelector(isHalfHalfPizza, group);

                            // Get portion options from group or use defaults (check for empty array too)
                            const portionOptionsMultiple = group.portionOptions?.length ? group.portionOptions : [
                              { key: "left", label: "Left" },
                              { key: "right", label: "Right" },
                              { key: "whole", label: "Whole" },
                            ];

                            return (
                              <div key={idx}>
                                {showHalfHalfOptions ? (
                                  // Half & Half Pizza UI with section buttons for multiple selection
                                  <div className="flex flex-col w-full space-y-2">
                                    <div className="flex items-center space-x-3">
                                      {choice.image && (
                                        <img
                                          src={choice.image}
                                          alt={choice.label}
                                          className="w-14 h-14 object-cover rounded-md"
                                        />
                                      )}
                                      <div className="flex flex-col w-full mt-1">
                                        <div className="flex flex-row w-full justify-between mb-2">
                                          <p className="text-sm font-medium m-0">
                                            {choice.label}
                                            {selectedSection && (
                                              <span className="text-xs text-black ml-1">
                                                -{" "}
                                                {selectedSection
                                                  .charAt(0)
                                                  .toUpperCase() +
                                                  selectedSection.slice(1)}
                                              </span>
                                            )}
                                          </p>
                                          <p className="text-sm font-medium m-0">
                                            {choice.price > 0 &&
                                              `${currencySymbol}${choice.price.toFixed(2)}`}
                                          </p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Pizza section buttons */}
                                    <div className="flex gap-2 mt-0">
                                      {portionOptionsMultiple.map((option) => (
                                        <button
                                          key={option.key}
                                          onClick={() =>
                                            handleHalfHalfSelection(
                                              key,
                                              option.key as "left" | "right" | "whole",
                                            )
                                          }
                                         className={`flex items-center justify-center gap-2 px-4 py-2 rounded-xl border-2 text-sm font-semibold transition-all duration-200 min-w-[80px] shadow-sm ${
                                            selectedSection === option.key
                                              ? "bg-black text-white scale-105 border-black"
                                              : "bg-white text-black hover:bg-gray-100 hover:scale-105 border-black/30"
                                          }`}
                                        >
                                          {PizzaIcons[option.key as keyof typeof PizzaIcons] || null}
                                          <span className="font-bold">
                                            {option.label}
                                          </span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  // Regular checkbox UI for non-Half & Half items
                                  <div className={`flex items-center p-1 gap-1 ${isLocked ? "opacity-60" : ""}`}>
                                    <label className={`relative flex items-center ${isLocked || isChoiceMaxedOut ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}>
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        disabled={isLocked || isChoiceMaxedOut}
                                        onChange={() => {
                                          if (!isLocked && !isChoiceMaxedOut) handleChoiceChange(key, isChecked ? 0 : 1);
                                        }}
                                        className="w-4 h-4 accent-black rounded border border-gray-300 focus:outline-none focus:ring-0"
                                      />
                                      <svg
                                        className="absolute w-3 h-3 text-white pointer-events-none left-[3px] top-[3px] opacity-0 peer-checked:opacity-100"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                        fill="white"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-7.364 7.364a1 1 0 01-1.414 0L3.293 9.414a1 1 0 011.414-1.414l4.121 4.121 6.657-6.657a1 1 0 011.414 0z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </label>

                                    <div className="flex items-center w-full space-x-3">
                                      {choice.image && (
                                        <img
                                          src={choice.image}
                                          alt={choice.label}
                                          className="w-14 h-14 object-cover rounded-md"
                                        />
                                      )}
                                      <div className="flex flex-row w-full justify-between">
                                        <p
                                          className={`text-sm font-medium m-0 ${isLocked || isChoiceMaxedOut ? "cursor-not-allowed" : "cursor-pointer"}`}
                                          onClick={() => {
                                            if (!isLocked && !isChoiceMaxedOut) handleChoiceChange(key, isChecked ? 0 : 1);
                                          }}
                                        >
                                          {choice.label}
                                          {isLocked && (
                                            <span className="text-xs text-gray-500 ml-1">(Included)</span>
                                          )}
                                        </p>
                                        <p className="text-sm font-medium m-0">
                                          {!isLocked && choice.price > 0 &&
                                            `${currencySymbol}${choice.price.toFixed(2)}`}
                                          {isLocked && (
                                            <span className="text-xs text-green-600 font-medium">Included</span>
                                          )}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          });
                        }

                        // Default: quantity selection (number buttons) - Optional selections
                        return [...group.choices].sort((a, b) => (b.default ? 1 : 0) - (a.default ? 1 : 0)).map((choice, idx) => {
                          const key = `${group.name}|||${choice.label}`;
                          const quantity =
                            (customSelections[key] as number) || 0;
                          const isHiddenByMax =
                            !choice.default &&
                            hasReachedMaxToppings &&
                            quantity === 0;
                          if (isHiddenByMax) return null;
                          const selectedSection =
                            (customSelections[`${key}_section`] as string) ||
                            "";

                          // Use group's showPortionSelector setting, fallback to old logic for backward compatibility
                   const showHalfHalfOptions = shouldShowPortionSelector(isHalfHalfPizza, group);

                          // Get quantity options from group or use defaults (check for empty array too)
                          const quantityOptions = group.quantityOptions?.length ? group.quantityOptions : [0, 1, 2, 3];
                          const isDefaultWithOffset = choice.default && quantityOptions[0] < 0;
                          const showPortionForChoice = isDefaultWithOffset
                            ? quantity >= 0
                            : quantity > 0;
                          // Get portion options from group or use defaults (check for empty array too)
                          const portionOptions = group.portionOptions?.length ? group.portionOptions : [
                            { key: "left", label: "Left" },
                            { key: "whole", label: "Whole" },
                            { key: "right", label: "Right" },
                          ];

                          if (choice.default) {
                            return (
                              <div key={idx}>
                                <div className="flex items-center space-x-3 mb-2">
                                  {choice.image && (
                                    <img
                                      src={choice.image}
                                      alt={choice.label}
                                      className="w-14 h-14 object-cover border-[1.5px] border-gray-300 rounded-md"
                                    />
                                  )}
                                  <div className="flex flex-col w-full">
                                    <div className="flex flex-row w-full justify-between">
                                      <p className="text-sm font-semibold line-clamp-2 mb-0">
                                        {choice.label}
                                        {selectedSection && showPortionForChoice && (
                                          <span className="text-xs text-black ml-1">
                                            -{" "}
                                            {selectedSection
                                              .charAt(0)
                                              .toUpperCase() +
                                              selectedSection.slice(1)}
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                {/* Quantity buttons */}
                                <div className="flex items-center gap-3 flex-wrap mb-3">
                                  {quantityOptions.map((num, btnIdx) => {
                                    // For default choices: if first option is negative (like -1), treat as "remove"
                                    // Otherwise use direct mapping
                                    const actualQuantity =
                                      choice.default && !isDefaultWithOffset
                                        ? num === 0
                                          ? -1
                                          : num
                                        : num;
                                    const displayNum = isDefaultWithOffset ? btnIdx : num;
                                    const chargeableQty = choice.default
                                      ? isDefaultWithOffset
                                        ? Math.max(0, actualQuantity)
                                        : Math.max(0, actualQuantity - 1)
                                      : Math.max(0, actualQuantity);
                                    const additionalPrice =
                                      chargeableQty * (choice.price || 0);
                                    const isSelected =
                                      quantity === actualQuantity;

                                    return (
                                      <button
                                        key={num}
                                        onClick={() =>
                                          handleChoiceChange(
                                            key,
                                            actualQuantity,
                                          )
                                        }
                                       className={`grid place-items-center rounded-lg border transition-all w-[70px] h-[50px] ${
                                          isSelected
                                            ? "border-black bg-black text-white"
                                            : "border-black/30 bg-white text-black"
                                        }`}
                                      >
                                        <div className="flex flex-col items-center justify-center">
                                          <span className="text-lg font-bold leading-none">
                                            {displayNum}
                                          </span>
                                          {additionalPrice > 0 && (
                                            <span className={`text-xs mt-1 ${isSelected ? "text-white" : "text-gray-600"}`}>
                                              +${additionalPrice.toFixed(2)}
                                            </span>
                                          )}
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>

                                {/* Half & Half options for default choices when quantity > 0 */}
                                {showHalfHalfOptions && showPortionForChoice && (
                                  <div>
                                    <div className="flex mb-4 border-2 border-gray-300 rounded-lg overflow-hidden w-fit">
                                      {portionOptions.map((option, index) => (
                                        <div
                                          key={option.key}
                                          className={`flex ${
                                            index !== portionOptions.length - 1
                                              ? "border-r border-gray-300"
                                              : ""
                                          }`}
                                        >
                                          <button
                                            onClick={() =>
                                              handleHalfHalfSelection(
                                                key,
                                                option.key as "left" | "right" | "whole",
                                              )
                                            }
                                          className={`flex flex-col items-center justify-center gap-1 px-4 py-2 text-xs font-semibold transition-all duration-200 min-w-[70px]
                                            ${selectedSection === option.key
                                              ? "bg-black text-white scale-105"
                                              : "bg-white text-black hover:bg-gray-100 "
                                            }`}
                                          >
                                            {PizzaIcons[option.key as keyof typeof PizzaIcons] || null}
                                            <span className="font-bold">
                                              {option.label}
                                            </span>
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          }

                          return (
                            <div
                              key={idx}
                              className="my-2"
                            >
                              <div className="flex items-center space-x-3 mb-2">
                                {choice.image && (
                                  <img
                                    src={choice.image}
                                    alt={choice.label}
                                    className="w-14 h-14 object-cover border-[1.5px] border-gray-300 rounded-md"
                                  />
                                )}
                                <div className="flex flex-col w-full mt-1">
                                  <div className="flex flex-row w-full justify-between">
                                    <p className={`text-sm font-semibold line-clamp-2 mb-0 ${!!maxToppings && totalSelectedInGroup >= maxToppings && quantity === 0 ? "opacity-40" : ""}`}>
                                      {choice.label}
                                      {selectedSection && showPortionForChoice && (
                                        <span className="text-xs text-gray-500 ml-1">
                                          -{" "}
                                          {selectedSection
                                            .charAt(0)
                                            .toUpperCase() +
                                            selectedSection.slice(1)}
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Quantity buttons for non-default choices */}
                              <div className="flex items-center gap-3 flex-wrap mb-3">
                                {quantityOptions.map((num) => {
                                  const actualQuantity = num; // use num directly for non-default
                                  const isDefaultWithOffset =
                                    choice.default && quantityOptions[0] < 0;
                                  const chargeableQty = choice.default
                                    ? isDefaultWithOffset
                                      ? Math.max(0, actualQuantity)
                                      : Math.max(0, actualQuantity - 1)
                                    : Math.max(0, actualQuantity);
                                  const availableFreeForChoice = freeBeforeChoice.get(key) ?? 0;
                                  const freeUnits = Math.min(chargeableQty, availableFreeForChoice);
                                  const displayPrice = (chargeableQty - freeUnits) * (choice.price || 0);
                                  const isFreeButton = chargeableQty > 0 && freeUnits >= chargeableQty;
                                  const isSelected =
                                    quantity === actualQuantity;
                                  const isButtonDisabled =
                                    !!maxToppings &&
                                    num > 0 &&
                                    totalSelectedInGroup - quantity + num > maxToppings;

                                  return (
                                    <button
                                      key={num}
                                      disabled={isButtonDisabled}
                                      onClick={() => {
                                        if (!isButtonDisabled) handleChoiceChange(key, actualQuantity);
                                      }}
                                     className={`grid place-items-center rounded-lg border transition-all w-[70px] h-[50px] ${
                                        isButtonDisabled
                                          ? "border-black/10 bg-gray-50 text-black/20 cursor-not-allowed"
                                          : isSelected
                                            ? "border-black bg-black text-white"
                                            : "border-gray-300 bg-white text-black hover:border-gray-400"
                                      }`}
                                    >
                                      <div className="flex flex-col items-center justify-center">
                                        <span className="text-lg font-bold leading-none">
                                          {num}
                                        </span>
                                        {!isFreeButton && displayPrice > 0 ? (
                                          <span className={`text-xs mt-1 ${isSelected ? "text-white" : "text-gray-600"}`}>
                                            +${displayPrice.toFixed(2)}
                                          </span>
                                        ) : null}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>

                              {/* Half & Half options for non-default choices when quantity > 0 */}
                              {showHalfHalfOptions && showPortionForChoice && (
                                <div>
                                  <div className="flex mb-4 border-2 border-gray-300 rounded-lg overflow-hidden w-fit">
                                    {portionOptions.map((option, index) => (
                                      <div
                                        key={option.key}
                                        className={`flex ${
                                          index !== portionOptions.length - 1
                                            ? "border-r border-gray-300"
                                            : ""
                                        }`}
                                      >
                                        <button
                                          onClick={() =>
                                            handleHalfHalfSelection(
                                              key,
                                              option.key as "left" | "right" | "whole",
                                            )
                                          }
                                        className={`flex flex-col items-center justify-center gap-1 px-4 py-2 text-xs font-semibold transition-all duration-200 min-w-[70px]
                                          ${selectedSection === option.key
                                            ? "bg-black text-white scale-105"
                                            : "bg-white text-black hover:bg-gray-100 "
                                          }`}
                                        >
                                          {PizzaIcons[option.key as keyof typeof PizzaIcons] || null}
                                          <span className="font-bold">
                                            {option.label}
                                          </span>
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                    </div>
                    </div>
                  </div>
                  );
                });
              })()}
            </div>
          </Modal.Body>

          <Modal.Footer className="sticky bottom-0 bg-white m-0">
            {/* Left side: Total */}
            <div className="flex justify-between w-full items-center">
            <div className="text-lg font-semibold">
  {activeBundleDeal ? `Extras: ${currencySymbol}` : `Total: ${currencySymbol}`}
  {(() => {
    const basePrice = activeBundleDeal
      ? 0  // base is included in bundle price; only extras charged on top
      : activeBYODeal
        ? activeBYODeal.basePrice
        : editingDealMeta?.dealType === 'build_your_own' && editingDealMeta.byoBasePrice !== undefined
          ? editingDealMeta.byoBasePrice
          : editingDealMeta?.dealType === 'bundle'
            ? 0  // extras only, same as active bundle
            : (selectedCustomizeItem?.price || 0);
    let extraPrice = 0;

    Object.entries(customSelections).forEach(([key, quantity]) => {
      if (key.endsWith("_section")) return;
      // Locked preselections are included in bundle price — not an extra charge
      if (activeBundleDeal && lockedKeys.has(key)) return;
      const [groupName, choiceLabel] = key.split("|||");
      const group = selectedCustomizeItem?.customize.find((g) => g.name === groupName);
      const choice = group?.choices.find((c) => c.label === choiceLabel);
      const qty = (quantity as number) || 0;
      if (qty <= 0) return;
      const quantityOptions = group?.quantityOptions?.length
        ? group.quantityOptions
        : [0, 1, 2, 3];
      const hasDefaultOffset = !!choice?.default && quantityOptions[0] < 0;
      const chargeableQty = choice?.default
        ? hasDefaultOffset
          ? Math.max(0, qty)
          : Math.max(0, qty - 1)
        : qty;
      if (chargeableQty <= 0) return;
      extraPrice += (choice?.price || 0) * chargeableQty;
    });

    // Apply free topping discount — BYO uses includedToppings, normal items use noOfFreeToppings
    const byoIncluded = activeBYODeal?.includedToppings ?? editingDealMeta?.byoIncludedToppings;
    if ((activeBYODeal || editingDealMeta?.dealType === 'build_your_own') && byoIncluded !== undefined && selectedCustomizeItem) {
      let freeRem = byoIncluded;
      let byoDiscount = 0;
      for (const group of selectedCustomizeItem.customize) {
        if (group.required || group.multiple || freeRem <= 0) continue;
        for (const choice of group.choices) {
          if (freeRem <= 0) break;
          if (choice.default) continue;
          const key = `${group.name}|||${choice.label}`;
          const qty = Number((customSelections as Record<string, number>)[key] || 0);
          if (qty > 0) {
            const freeQty = Math.min(qty, freeRem);
            byoDiscount += (choice.price || 0) * freeQty;
            freeRem -= freeQty;
          }
        }
      }
      extraPrice = Math.max(0, extraPrice - byoDiscount);
    } else if (!activeBYODeal && selectedCustomizeItem) {
      extraPrice = Math.max(0, extraPrice - computeFreeToppingDiscount(selectedCustomizeItem, customSelections as Record<string, number | string>));
    }

    return (basePrice + extraPrice).toFixed(2);
  })()}
</div>
              <div
                className={`text-center text-[15px] text-white px-3 py-[6px] rounded-[10px] font-semibold ${
                  isCustomizationValid
                    ? "cursor-pointer bg-main"
                    : "cursor-pointer bg-gray-400"
                }`}
               onClick={(e) => {
                e.stopPropagation();
                if (!isCustomizationValid) {
                  setHasAttemptedSubmit(true);  
                  focusFirstMissingRequiredGroup();
                  return;
                }
                setHasAttemptedSubmit(false);  
                handleCustomSubmit();
                  const currentUrl = new URL(window.location.href);
                  currentUrl.searchParams.delete("item");
                  window.history.pushState({}, "", currentUrl.toString());
                }}
              >
                {activeBundleDeal && !( activeBundleDeal.currentSlotIndex === activeBundleDeal.slots.length - 1)
                  ? `NEXT (${activeBundleDeal.currentSlotIndex + 1}/${activeBundleDeal.slots.length})`
                  : 'Add to Cart'}
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      )}
      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.isDeal ? "Remove Deal?" : "Remove Item?"}
        message={
          confirmModal.isDeal
            ? `This will remove the entire  "${confirmModal.itemName}"${confirmModal.itemCount ? ` (${confirmModal.itemCount} items)` : ""} deal from your cart. Continue?`
            : `Are you sure you want to remove "${confirmModal.itemName}" from your cart?`
        }
        onConfirm={handleConfirmRemove}
        onCancel={() => setConfirmModal({ open: false })}
      />
    </div>
  );
};

export default POS;
